/* global globalThis */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Rating,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import {
  Add,
  ArrowBack,
  CheckCircle,
  Close,
  ContentCopy,
  Delete,
  Favorite,
  FavoriteBorder,
  Home,
  ListAlt,
  LocationOn,
  LocalShipping,
  MyLocation,
  Directions,
  Logout,
  Notifications,
  Person,
  Remove,
  Restaurant,
  Search,
  ShoppingCart,
  Storefront,
  Chat,
  Send,
  Email,
  Lock,
  ArrowForward,
  OpenInNew,
  CreditCard
} from '@mui/icons-material';
import WebPushButton from './WebPushButton';
import OneSignalWebService from '../services/OneSignalWebService';
import municipalities from '../data/municipios-brasil.json';

const API_URL = process.env.REACT_APP_API_URL || 'https://all-delivery.onrender.com/api';
const API_ORIGIN = API_URL.replace(/\/api\/?$/, '');
const APP_DELIVERY_FEE = 4;
const MANUAL_DELIVERY_FEE_ID = 'manual-delivery-fee';
const STORAGE_PREFIX = '@DeliveryAppWeb';
const DEVICE_ID_KEY = `${STORAGE_PREFIX}:deviceId`;
const normalizeCitySearch = (value) => String(value || '')
  .replace(/[\s]*[-–,][\s]*[a-z]{2}$/i, '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLocaleLowerCase('pt-BR');
const PENDING_ORDER_KEY = `${STORAGE_PREFIX}:pendingOrder`;
// O client ID OAuth nao e segredo. O fallback garante que o botao continue
// disponivel em builds feitos fora do Render/Netlify sem a variavel do frontend.
const GOOGLE_WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID
  || '906885966931-m1mp58h62pmp6cpmcta859ufd4qhveqa.apps.googleusercontent.com';

async function checkoutFingerprint(payload) {
  const serialized = JSON.stringify(payload);
  if (globalThis.crypto?.subtle && typeof TextEncoder !== 'undefined') {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(serialized));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  // Compatibilidade com navegadores antigos sem Web Crypto.
  let first = 0x811c9dc5;
  let second = 0x9e3779b9;
  for (let index = 0; index < serialized.length; index += 1) {
    const code = serialized.charCodeAt(index);
    first = Math.imul(first ^ code, 0x01000193);
    second = Math.imul(second ^ code, 0x85ebca6b);
  }
  return `${(first >>> 0).toString(16)}${(second >>> 0).toString(16)}`;
}

function secureIdempotencyId() {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  if (cryptoApi?.getRandomValues) {
    return Array.from(cryptoApi.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  throw new Error('Este navegador nao oferece um gerador seguro para finalizar o pedido. Atualize o navegador e tente novamente.');
}

function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000
});

function assetUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

function money(value) {
  return `R$ ${(Number(value) || 0).toFixed(2)}`;
}

function getSizeOptions(product) {
  return [
    Number(product?.price_p) > 0 ? { label: 'P', price: Number(product.price_p) } : null,
    Number(product?.price_g) > 0 ? { label: 'G', price: Number(product.price_g) } : null
  ].filter(Boolean);
}

function getProductPrice(product, size = null) {
  return Number(size?.price) || getSizeOptions(product)[0]?.price || Number(product?.price) || 0;
}

function productPriceLabel(product) {
  const sizes = getSizeOptions(product);
  return sizes.length ? sizes.map((size) => `${size.label}: ${money(size.price)}`).join(' / ') : money(product?.price);
}

function deliveryMode(lanchonete) {
  return String(lanchonete?.delivery_mode || 'LOJA').toUpperCase() === 'APP' ? 'APP' : 'LOJA';
}

function deliveryFee(lanchonete, paymentMethod) {
  if (paymentMethod === 'pickup') return 0;
  return deliveryMode(lanchonete) === 'APP' ? APP_DELIVERY_FEE : Number(lanchonete?.delivery_fee) || 0;
}

function normalizeCity(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function normalizePaymentValue(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function extractCityFromAddress(address) {
  const text = String(address || '').trim();
  if (!text) return '';
  const dashParts = text.split('-');
  if (dashParts.length >= 2) return dashParts[dashParts.length - 2].split(',').pop().trim();
  const commaParts = text.split(',');
  if (commaParts.length > 1) return commaParts[commaParts.length - 2].trim();
  return text;
}

function statusText(status) {
  const map = {
    pendente: 'Pendente',
    pending: 'Pendente',
    payment_pending: 'Aguardando pagamento',
    aceito: 'Aceito',
    accepted: 'Aceito',
    preparando: 'Preparando',
    preparing: 'Preparando',
    pronto: 'Pronto',
    saiu_entrega: 'Saiu para entrega',
    delivering: 'Saiu para entrega',
    entregue: 'Entregue',
    delivered: 'Entregue',
    cancelado: 'Cancelado',
    cancelled: 'Cancelado'
  };
  return map[status] || status || 'Pendente';
}

function statusColor(status) {
  if (['cancelado', 'cancelled'].includes(status)) return '#ef4444';
  if (['entregue', 'delivered'].includes(status)) return '#16a34a';
  if (['payment_pending', 'pending', 'pendente'].includes(status)) return '#f59e0b';
  if (['saiu_entrega', 'delivering'].includes(status)) return '#7c3aed';
  if (['preparando', 'preparing', 'aceito', 'accepted', 'pronto'].includes(status)) return '#2563eb';
  return '#10B981';
}

function orderNeedsPayment(order) {
  return String(order?.status || '').toLowerCase() === 'payment_pending';
}

function orderHasPaymentLink(order) {
  return Boolean(order?.payment_link);
}

function orderIsCardPayment(order) {
  const method = normalizePaymentValue(order?.payment_method);
  return ['card', 'cartao', 'credit_card', 'credito', 'debit_card'].includes(method);
}

function orderHasPixPayment(order) {
  return Boolean(order?.pix_qr_code || order?.pix_qr_code_base64);
}

const CustomerAuthContext = createContext(null);
const CartContext = createContext(null);

function GoogleWebButton({ onCredential, disabled = false }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!GOOGLE_WEB_CLIENT_ID || disabled) return undefined;
    let cancelled = false;
    let timer = null;
    const renderButton = () => {
      if (cancelled || !containerRef.current || !window.google?.accounts?.id) return false;
      containerRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: GOOGLE_WEB_CLIENT_ID,
        callback: ({ credential }) => credential && onCredential(credential),
        auto_select: false
      });
      window.google.accounts.id.renderButton(containerRef.current, {
        type: 'standard', theme: 'outline', size: 'large', text: 'continue_with',
        shape: 'rectangular', logo_alignment: 'left', width: Math.min(360, containerRef.current.clientWidth || 320)
      });
      return true;
    };
    if (!renderButton()) {
      let script = document.querySelector('script[data-all-delivery-google]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.dataset.allDeliveryGoogle = 'true';
        document.head.appendChild(script);
      }
      script.addEventListener('load', renderButton, { once: true });
      timer = window.setInterval(() => renderButton() && window.clearInterval(timer), 500);
    }
    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [onCredential, disabled]);

  if (!GOOGLE_WEB_CLIENT_ID) return null;
  return <Box ref={containerRef} sx={{ display: 'flex', justifyContent: 'center', minHeight: 44, opacity: disabled ? 0.6 : 1 }} />;
}

function CustomerAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [guestMode, setGuestMode] = useState(() => localStorage.getItem(`${STORAGE_PREFIX}:guestMode`) === 'true');
  const [loading, setLoading] = useState(true);

  const setTokens = (token, refreshToken, userData) => {
    localStorage.removeItem(`${STORAGE_PREFIX}:guestMode`);
    setGuestMode(false);
    localStorage.setItem(`${STORAGE_PREFIX}:token`, token || '');
    localStorage.setItem(`${STORAGE_PREFIX}:refreshToken`, refreshToken || '');
    localStorage.setItem(`${STORAGE_PREFIX}:user`, JSON.stringify(userData || null));
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = async () => {
    OneSignalWebService.logout().catch((error) => {
      console.warn('[OneSignal Web] logout:', error.message);
    });
    const refreshToken = localStorage.getItem(`${STORAGE_PREFIX}:refreshToken`);
    if (refreshToken) {
      await api.post('/auth/logout', { refreshToken, device_id: getDeviceId() }).catch(() => {});
    }
    localStorage.removeItem(`${STORAGE_PREFIX}:token`);
    localStorage.removeItem(`${STORAGE_PREFIX}:refreshToken`);
    localStorage.removeItem(`${STORAGE_PREFIX}:user`);
    delete api.defaults.headers.common.Authorization;
    setUser(null);
  };

  const continueAsGuest = () => {
    localStorage.removeItem(`${STORAGE_PREFIX}:token`);
    localStorage.removeItem(`${STORAGE_PREFIX}:refreshToken`);
    localStorage.removeItem(`${STORAGE_PREFIX}:user`);
    delete api.defaults.headers.common.Authorization;
    setUser(null);
    localStorage.setItem(`${STORAGE_PREFIX}:guestMode`, 'true');
    setGuestMode(true);
  };

  const leaveGuestMode = () => {
    localStorage.removeItem(`${STORAGE_PREFIX}:guestMode`);
    setGuestMode(false);
  };

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config || {};
        if (error.response?.status === 401 && !original._retry) {
          original._retry = true;
          const refreshToken = localStorage.getItem(`${STORAGE_PREFIX}:refreshToken`);
          if (refreshToken) {
            try {
              const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken, device_id: getDeviceId() });
              const nextToken = response.data.token;
              const nextRefresh = response.data.refreshToken;
              localStorage.setItem(`${STORAGE_PREFIX}:token`, nextToken);
              localStorage.setItem(`${STORAGE_PREFIX}:refreshToken`, nextRefresh);
              api.defaults.headers.common.Authorization = `Bearer ${nextToken}`;
              original.headers.Authorization = `Bearer ${nextToken}`;
              return api(original);
            } catch (_) {
              // Preserve the stored session across temporary API or refresh failures.
            }
          }
        }
        return Promise.reject(error);
      }
    );

    const token = localStorage.getItem(`${STORAGE_PREFIX}:token`);
    const storedUser = localStorage.getItem(`${STORAGE_PREFIX}:user`);
    if (token && storedUser) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);

    return () => api.interceptors.response.eject(interceptor);
  }, []);

  const login = async (email, password, loginMode = 'client') => {
    try {
      const response = await api.post('/auth/login', { email: email.trim(), password, device_id: getDeviceId() });
      const userData = response.data.user;
      if (loginMode === 'client' && userData?.user_type !== 'client') {
        return { success: false, error: 'Este acesso e somente para clientes.' };
      }
      if (loginMode === 'motoboy' && userData?.user_type !== 'motoboy') {
        return { success: false, error: 'Este acesso e somente para motoboys.' };
      }
      if (loginMode === 'waiter' && userData?.user_type !== 'waiter') {
        return { success: false, error: 'Este acesso e somente para garcons.' };
      }
      if (!['client', 'waiter', 'motoboy'].includes(userData?.user_type)) {
        return { success: false, error: 'Use o painel administrativo para este tipo de conta.' };
      }
      setTokens(response.data.token, response.data.refreshToken, userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Erro ao entrar' };
    }
  };

  const register = async (payload) => {
    try {
      const response = await api.post('/auth/register', { ...payload, device_id: getDeviceId() });
      if (response.data?.pending_approval) {
        return { success: true, pendingApproval: true, message: response.data.message };
      }
      setTokens(response.data.token, response.data.refreshToken, response.data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Erro ao criar conta' };
    }
  };

  const googleLogin = async (idToken) => {
    try {
      const response = await api.post('/auth/google', { id_token: idToken, device_id: getDeviceId() });
      setTokens(response.data.token, response.data.refreshToken, response.data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Erro ao entrar com Google' };
    }
  };

  const updateProfile = async (payload) => {
    try {
      const response = await api.put('/users/profile', payload);
      const nextUser = response.data.user || response.data;
      localStorage.setItem(`${STORAGE_PREFIX}:user`, JSON.stringify(nextUser));
      setUser(nextUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Erro ao atualizar perfil' };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.put('/users/password', { currentPassword, newPassword });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Erro ao alterar senha' };
    }
  };

  const deleteAccount = async (password) => {
    try {
      await api.delete('/users/me', { data: { password } });
      await logout();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Nao foi possivel excluir a conta' };
    }
  };

  return (
    <CustomerAuthContext.Provider value={{ user, guestMode, loading, login, googleLogin, register, logout, continueAsGuest, leaveGuestMode, updateProfile, changePassword, deleteAccount }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

function useCustomerAuth() {
  return useContext(CustomerAuthContext);
}

function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}:cart`) || '[]');
    } catch (_) {
      return [];
    }
  });

  const save = (nextItems) => {
    setItems(nextItems);
    localStorage.setItem(`${STORAGE_PREFIX}:cart`, JSON.stringify(nextItems));
  };

  const calculateUnitPrice = (item) => {
    const extras = (item.customizations || []).reduce(
      (total, group) => total + (group.selectedOptions || []).reduce((sum, option) => sum + (Number(option.price) || 0), 0),
      0
    );
    const hasHalfPizza = (item.customizations || []).some((group) => group.groupId === 'pizza-flavors');
    const wholesaleMinimum = Number(item.wholesale_min_qty) || 0;
    const wholesalePrice = Number(item.wholesale_price) || 0;
    const hasWholesalePrice = !hasHalfPizza && wholesaleMinimum > 0 && wholesalePrice > 0 && Number(item.quantity) >= wholesaleMinimum;
    return (hasWholesalePrice ? wholesalePrice : Number(item.price) || 0) + extras;
  };

  const addToCart = (produto, quantity, customizations = [], observation = '') => {
    const key = `${produto.id}-${JSON.stringify(customizations)}-${observation}`;
    const existing = items.findIndex((item) => item.key === key);
    const nextItems = [...items];
    if (existing >= 0) {
      const updatedItem = {
        ...nextItems[existing],
        price: Number(produto.price) || 0,
        wholesale_min_qty: Number(produto.wholesale_min_qty) || null,
        wholesale_price: Number(produto.wholesale_price) || null,
        quantity: nextItems[existing].quantity + quantity
      };
      updatedItem.unit_price = calculateUnitPrice(updatedItem);
      updatedItem.subtotal = updatedItem.quantity * updatedItem.unit_price;
      nextItems[existing] = updatedItem;
    } else {
      const newItem = {
        key,
        produto_id: produto.id,
        name: produto.name,
        price: Number(produto.price) || 0,
        wholesale_min_qty: Number(produto.wholesale_min_qty) || null,
        wholesale_price: Number(produto.wholesale_price) || null,
        unit_price: 0,
        quantity,
        subtotal: 0,
        image: produto.image,
        lanchonete_id: produto.lanchonete_id,
        size_label: produto.size_label || null,
        customizations,
        observation,
        is_manual_delivery_fee: produto.is_manual_delivery_fee === true
      };
      newItem.unit_price = calculateUnitPrice(newItem);
      newItem.subtotal = newItem.unit_price * quantity;
      nextItems.push(newItem);
    }
    save(nextItems);
  };

  const updateQuantity = (key, quantity) => {
    if (quantity <= 0) {
      save(items.filter((item) => item.key !== key));
      return;
    }
    save(items.map((item) => {
      if (item.key !== key) return item;
      const updatedItem = { ...item, quantity };
      updatedItem.unit_price = calculateUnitPrice(updatedItem);
      updatedItem.subtotal = updatedItem.unit_price * quantity;
      return updatedItem;
    }));
  };

  const clearCart = () => save([]);
  const replaceCart = (nextItems) => save(Array.isArray(nextItems) ? nextItems : []);
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + calculateUnitPrice(item) * item.quantity, 0);
  const lanchoneteId = items[0]?.lanchonete_id || null;
  const isFromSameLanchonete = (id) => !lanchoneteId || Number(lanchoneteId) === Number(id);

  return (
    <CartContext.Provider value={{ items, addToCart, updateQuantity, clearCart, replaceCart, totalItems, subtotal, lanchoneteId, isFromSameLanchonete, calculateUnitPrice }}>
      {children}
    </CartContext.Provider>
  );
}

function useCart() {
  return useContext(CartContext);
}

function CustomerLogin() {
  const { login, googleLogin, register, continueAsGuest } = useCustomerAuth();
  const [mode, setMode] = useState('login');
  const [loginMode, setLoginMode] = useState('client');
  const [accountType, setAccountType] = useState('client');
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', address: '', house_number: '', reference: '', vehicle_type: 'motorcycle', vehicle_plate: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState('request');
  const [recovery, setRecovery] = useState({ email: '', code: '', password: '' });
  const [recoveryOptions, setRecoveryOptions] = useState({});
  const [recoveryMessage, setRecoveryMessage] = useState('');

  const openRecovery = async () => {
    setRecovery({ email: form.email, code: '', password: '' });
    setRecoveryStep('request');
    setRecoveryMessage('');
    setRecoveryOpen(true);
    try {
      const response = await api.get('/auth/recovery-options');
      const options = response.data || {};
      setRecoveryOptions(options);
    } catch (_) {
      setRecoveryMessage('Nao foi possivel consultar os canais de recuperacao.');
    }
  };

  const sendRecovery = async () => {
    try {
      const response = await api.post('/auth/forgot-password', { email: recovery.email });
      setRecoveryMessage(response.data.message);
      setRecoveryStep('reset');
    } catch (err) {
      setRecoveryMessage(err.response?.data?.error || 'Nao foi possivel enviar o codigo.');
    }
  };

  const finishRecovery = async () => {
    try {
      const response = await api.post('/auth/reset-password', {
        email: recovery.email, code: recovery.code, new_password: recovery.password
      });
      setForm({ ...form, email: recovery.email, password: '' });
      setRecoveryMessage(response.data.message);
      setRecoveryStep('done');
    } catch (err) {
      setRecoveryMessage(err.response?.data?.error || 'Nao foi possivel alterar a senha.');
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');
    const result = mode === 'login'
      ? await login(form.email, form.password, loginMode)
      : await register({
          name: form.name,
          email: form.email,
          password: form.password,
          phone: form.phone,
          address: form.address,
          house_number: form.house_number,
          reference: form.reference,
          user_type: accountType,
          vehicle_type: accountType === 'motoboy' ? form.vehicle_type : undefined,
          vehicle_plate: accountType === 'motoboy' ? form.vehicle_plate : undefined
        });
    setLoading(false);
    if (result.pendingApproval) {
      setSuccessMessage(result.message || 'Cadastro recebido. Aguarde a aprovação do Admin Master para acessar as entregas.');
      setMode('login');
      setLoginMode('motoboy');
      setAccountType('motoboy');
      setForm((current) => ({ ...current, password: '' }));
    } else if (!result.success) {
      setError(result.error);
    }
  };

  const handleGoogleCredential = useCallback(async (credential) => {
    setLoading(true);
    setError('');
    const result = await googleLogin(credential);
    setLoading(false);
    if (!result.success) setError(result.error);
  }, [googleLogin]);

  const subtitle = mode === 'login'
    ? loginMode === 'motoboy'
      ? 'Acesso exclusivo para motoboy do APP'
      : loginMode === 'waiter'
        ? 'Acesso exclusivo para garcons da loja'
        : 'Use seu e-mail cadastrado'
    : 'Preencha seus dados para começar a pedir';

  return (
    <Box className="customer-auth">
      <Box className="customer-auth-shell">
        <Box className="customer-auth-header">
          <Box className="customer-auth-logo-ring">
            <Box className="customer-auth-logo-inner">
              <Restaurant fontSize="large" />
            </Box>
          </Box>
          <Typography className="customer-auth-brand">All Delivery</Typography>
          <Typography className="customer-auth-tagline">Peça rápido, acompanhe em tempo real</Typography>
        </Box>

        <Paper className="customer-auth-card" elevation={0}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={continueAsGuest}
            sx={{ mb: 2, py: 1.5, fontWeight: 900, borderRadius: '14px' }}
          >
            Pedir sem criar conta
          </Button>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
            Voce informa somente nome, WhatsApp e endereco ao finalizar o pedido.
          </Typography>
          <Divider sx={{ mb: 3 }}>ou entre na sua conta</Divider>
          <Box className="customer-auth-card-header">
            <Typography variant="h5">{mode === 'login' ? 'Entrar' : 'Criar conta'}</Typography>
            <Typography variant="body2">{subtitle}</Typography>
          </Box>

          {mode === 'register' && (
            <>
              <Box className="mode-switch" sx={{ mb: 2 }}>
                <Button className={accountType === 'client' ? 'active' : ''} onClick={() => setAccountType('client')}>Cliente</Button>
                <Button className={accountType === 'motoboy' ? 'active' : ''} onClick={() => setAccountType('motoboy')}>Motoboy</Button>
              </Box>
              <Alert severity={accountType === 'motoboy' ? 'info' : 'success'} sx={{ mb: 2, borderRadius: '14px' }}>
                {accountType === 'motoboy'
                  ? 'Cadastro para motoboy parceiro do All Delivery. Sua conta precisa ser aprovada pelo Admin Master antes de receber corridas. Motoboy exclusivo de uma loja deve ser cadastrado pela propria lanchonete.'
                  : 'Cadastro de cliente para fazer pedidos e acompanhar entregas.'}
              </Alert>
            </>
          )}

          {mode === 'register' && accountType === 'client' && (
            <Box className="register-guide">
              <Box><strong>1</strong><span>Seus dados</span></Box>
              <i />
              <Box><strong>2</strong><span>Endereço</span></Box>
              <i />
              <Box><strong>3</strong><span>Começar a pedir</span></Box>
              <Typography>É rápido: preencha os campos abaixo. Depois você poderá alterar seus dados no Perfil.</Typography>
            </Box>
          )}

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '14px' }}>{error}</Alert>}
          {successMessage && <Alert severity="success" sx={{ mb: 2, borderRadius: '14px' }}>{successMessage}</Alert>}

          {((mode === 'login' && loginMode === 'client') || (mode === 'register' && accountType === 'client')) && (
            <Box sx={{ mb: 2 }}>
              <GoogleWebButton onCredential={handleGoogleCredential} disabled={loading} />
              {GOOGLE_WEB_CLIENT_ID && <Divider sx={{ mt: 2 }}>ou use e-mail e senha</Divider>}
            </Box>
          )}

          <Box component="form" onSubmit={submit} className="auth-form">
            {mode === 'login' && (
              <Box className="mode-switch">
                {[
                  ['client', 'Cliente'],
                  ['motoboy', 'Motoboy'],
                  ['waiter', 'Garcom']
                ].map(([value, label]) => (
                  <Button
                    key={value}
                    className={loginMode === value ? 'active' : ''}
                    onClick={() => setLoginMode(value)}
                  >
                    {label}
                  </Button>
                ))}
              </Box>
            )}

            {mode === 'register' && (
              <>
                <Box className="auth-input-group">
                  <Typography className="auth-input-label">Nome</Typography>
                  <TextField
                    className="auth-input-field"
                    fullWidth
                    required
                    placeholder="Seu nome"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </Box>
                <Box className="auth-input-group">
                  <Typography className="auth-input-label">Telefone</Typography>
                  <TextField
                    className="auth-input-field"
                    fullWidth
                    required
                    placeholder="Seu telefone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </Box>
                <Box className="auth-input-group">
                  <Typography className="auth-input-label">Endereco</Typography>
                  <TextField
                    className="auth-input-field"
                    fullWidth
                    placeholder="Rua, bairro"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                  />
                </Box>
                <Box className="auth-input-group">
                  <Typography className="auth-input-label">Numero da casa</Typography>
                  <TextField
                    className="auth-input-field"
                    fullWidth
                    placeholder="Numero"
                    value={form.house_number}
                    onChange={(e) => setForm({ ...form, house_number: e.target.value })}
                  />
                </Box>
                <Box className="auth-input-group">
                  <Typography className="auth-input-label">Referencia</Typography>
                  <TextField
                    className="auth-input-field"
                    fullWidth
                    placeholder="Ponto de referencia"
                    value={form.reference}
                    onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  />
                </Box>
                {accountType === 'motoboy' && (
                  <>
                    <Box className="auth-input-group">
                      <Typography className="auth-input-label">Tipo de veiculo</Typography>
                      <TextField
                        select
                        SelectProps={{ native: true }}
                        className="auth-input-field"
                        fullWidth
                        required
                        value={form.vehicle_type}
                        onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
                      >
                        <option value="motorcycle">Moto</option>
                        <option value="bicycle">Bicicleta</option>
                        <option value="car">Carro</option>
                      </TextField>
                    </Box>
                    <Box className="auth-input-group">
                      <Typography className="auth-input-label">Placa {form.vehicle_type === 'bicycle' ? '(nao se aplica)' : '(opcional)'}</Typography>
                      <TextField
                        className="auth-input-field"
                        fullWidth
                        disabled={form.vehicle_type === 'bicycle'}
                        placeholder="Ex.: ABC1D23"
                        value={form.vehicle_plate}
                        onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value.toUpperCase() })}
                      />
                    </Box>
                  </>
                )}
              </>
            )}

            <Box className="auth-input-group">
              <Typography className="auth-input-label">Email</Typography>
              <TextField
                className="auth-input-field"
                fullWidth
                type="email"
                required
                placeholder="Seu email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email className="auth-input-icon" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            <Box className="auth-input-group">
              <Typography className="auth-input-label">Senha</Typography>
              <TextField
                className="auth-input-field"
                fullWidth
                type="password"
                required
                placeholder="Sua senha"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock className="auth-input-icon" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            <Button type="submit" variant="contained" fullWidth disabled={loading} className="auth-submit-btn">
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                <>
                  {mode === 'login' ? 'Entrar' : 'Criar conta'}
                  <ArrowForward sx={{ ml: 1, fontSize: 18 }} />
                </>
              )}
            </Button>

            {mode === 'login' && <Button onClick={openRecovery} sx={{ fontWeight: 800 }}>Esqueci minha senha</Button>}

            <Box className="auth-switch-row">
              <Typography>{mode === 'login' ? 'Não tem uma conta?' : 'Já tem uma conta?'}</Typography>
              <Button onClick={() => setMode(mode === 'login' ? 'register' : 'login')}>
                {mode === 'login' ? 'Cadastre-se' : 'Entrar'}
              </Button>
            </Box>
          </Box>
        </Paper>
        <Dialog open={recoveryOpen} onClose={() => setRecoveryOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>Recuperar senha</DialogTitle>
          <DialogContent>
            {recoveryMessage && <Alert severity={recoveryStep === 'done' ? 'success' : 'info'} sx={{ mb: 2 }}>{recoveryMessage}</Alert>}
            {recoveryStep === 'request' && <>
              <TextField fullWidth label="Email da conta" type="email" margin="normal" value={recovery.email} onChange={(e) => setRecovery({ ...recovery, email: e.target.value })} />
              {!recoveryOptions.email && <Alert severity="warning" sx={{ mt: 1 }}>A recuperacao por email ainda nao foi configurada.</Alert>}
              <Alert severity="info" sx={{ mt: 1 }}>O codigo sera enviado somente para o email cadastrado.</Alert>
            </>}
            {recoveryStep === 'reset' && <>
              <TextField fullWidth label="Codigo de 6 digitos" margin="normal" inputProps={{ maxLength: 6 }} value={recovery.code} onChange={(e) => setRecovery({ ...recovery, code: e.target.value.replace(/\D/g, '') })} />
              <TextField fullWidth label="Nova senha" type="password" margin="normal" helperText="Minimo de 6 caracteres" value={recovery.password} onChange={(e) => setRecovery({ ...recovery, password: e.target.value })} />
            </>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRecoveryOpen(false)}>{recoveryStep === 'done' ? 'Fechar' : 'Cancelar'}</Button>
            {recoveryStep === 'request' && <Button variant="contained" onClick={sendRecovery} disabled={!recoveryOptions.email}>Enviar codigo por email</Button>}
            {recoveryStep === 'reset' && <Button variant="contained" onClick={finishRecovery}>Alterar senha</Button>}
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

function HomeView({ go, lanchonetesOnly = false }) {
  const { user } = useCustomerAuth();
  const [lanchonetes, setLanchonetes] = useState([]);
  const [banners, setBanners] = useState([]);
  const [favorites, setFavorites] = useState({});
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [type, setType] = useState('all');
  const [city, setCity] = useState('');
  const [cityLabel, setCityLabel] = useState('Todas as cidades');
  const [cep, setCep] = useState('');
  const [showLocationEditor, setShowLocationEditor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [homeOrders, setHomeOrders] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [storesRes, bannersRes] = await Promise.all([
        api.get('/lanchonetes'),
        api.get('/banners/public/active').catch(() => ({ data: [] }))
      ]);
      setLanchonetes(Array.isArray(storesRes.data) ? storesRes.data : []);
      setBanners(Array.isArray(bannersRes.data) ? bannersRes.data : bannersRes.data ? [bannersRes.data] : []);
      if (user) {
        const [favRes, ordersRes] = await Promise.all([
          api.get('/favorites').catch(() => ({ data: [] })),
          api.get('/pedidos').catch(() => ({ data: [] }))
        ]);
        setFavorites((favRes.data || []).reduce((acc, item) => ({ ...acc, [item.lanchonete_id]: true }), {}));
        setHomeOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 50000);
    return () => clearInterval(timer);
  }, [load]);

  const applyCep = async () => {
    const clean = cep.replace(/\D/g, '');
    if (clean.length !== 8) {
      setMessage('Digite um CEP com 8 numeros.');
      return;
    }
    try {
      let found = '';
      const viaCep = await fetch(`https://viacep.com.br/ws/${clean}/json/`).then((r) => r.json()).catch(() => null);
      found = viaCep?.localidade || '';
      if (!found) {
        const brasilApi = await fetch(`https://brasilapi.com.br/api/cep/v1/${clean}`).then((r) => r.json()).catch(() => null);
        found = brasilApi?.city || '';
      }
      if (!found) {
        setMessage('CEP nao encontrado.');
        return;
      }
      setCity(normalizeCity(found));
      setCityLabel(found);
    } catch (_) {
      setMessage('Nao foi possivel buscar o CEP.');
    }
  };

  const toggleFavorite = async (event, id) => {
    event.stopPropagation();
    try {
      if (favorites[id]) {
        await api.delete(`/favorites/${id}`);
        setFavorites((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setMessage('Removido dos favoritos');
      } else {
        await api.post('/favorites', { lanchonete_id: id });
        setFavorites((prev) => ({ ...prev, [id]: true }));
        setMessage('Adicionado aos favoritos');
      }
    } catch (_) {
      setMessage('Entre novamente para gerenciar favoritos.');
    }
  };

  const filtered = lanchonetes.filter((item) => {
    const matchesSearch = `${item.name || ''} ${item.description || ''}`.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    if (city) {
      const itemCity = normalizeCity(item.city || extractCityFromAddress(item.address));
      if (itemCity && itemCity !== city) return false;
    }
    if (type !== 'all' && String(item.type || 'lanchonete').toLowerCase() !== type) return false;
    if (filter === 'open' && !item.is_open) return false;
    if (filter === 'closed' && item.is_open) return false;
    if (filter === 'favorites' && !favorites[item.id]) return false;
    return true;
  });
  const visibleStores = [...filtered].sort((a, b) => {
    const aOpen = Boolean(a.is_open && a.is_online !== false);
    const bOpen = Boolean(b.is_open && b.is_online !== false);
    if (aOpen !== bOpen) return aOpen ? -1 : 1;
    return String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR');
  });
  const activeOrder = homeOrders.find((order) =>
    Date.now() - new Date(order.created_at).getTime() < 24 * 60 * 60 * 1000 &&
    !['delivered', 'entregue', 'completed', 'concluido', 'cancelled', 'cancelado'].includes(String(order.status || '').toLowerCase())
  );
  const lastDeliveredOrder = homeOrders.find((order) =>
    ['delivered', 'entregue', 'completed', 'concluido'].includes(String(order.status || '').toLowerCase())
  );
  const homeOrderStoreName = (order) =>
    lanchonetes.find((store) => Number(store.id) === Number(order?.lanchonete_id))?.name || 'Seu pedido';

  return (
    <>
      <Box className={lanchonetesOnly ? 'lanchonetes-header' : 'app-header home-header'}>
        <Box className="header-row">
          <Box className="home-brand">
            <Box className="brand-mark small"><Restaurant /></Box>
            <Box>
              <Typography className="home-brand-name" fontWeight={900}>{lanchonetesOnly ? 'Lanchonetes' : 'All Delivery'}</Typography>
              {!lanchonetesOnly && <Typography className="home-brand-tagline">Seu delivery, do seu jeito</Typography>}
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            <WebPushButton user={user} />
            <IconButton className="header-icon" onClick={() => go('notifications')}><Notifications /></IconButton>
          </Box>
        </Box>
        {!lanchonetesOnly && (
          <>
            <Box className="home-welcome">
              <Typography className="home-welcome-eyebrow">OLÁ, {String(user?.name || 'CLIENTE').split(' ')[0].toUpperCase()}</Typography>
              <Typography className="home-welcome-title">O que vamos pedir hoje?</Typography>
            </Box>
            <Button className="location-row" onClick={() => setShowLocationEditor((value) => !value)}>
              <LocationOn fontSize="small" />
              <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                <Typography variant="caption">Entregar em</Typography>
                <Typography noWrap fontWeight={900}>{cityLabel}</Typography>
              </Box>
              <ArrowForward fontSize="small" />
            </Button>

            <TextField
              className="home-hero-search"
              fullWidth
              placeholder="Busque por loja, lanche, pizza..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />
          </>
        )}
      </Box>

      <Box className={lanchonetesOnly ? 'screen-body' : 'screen-body home-screen-body'}>
        {!lanchonetesOnly && showLocationEditor && (
          <Paper className="location-search-card">
            <Box className="location-search-icon"><LocationOn /></Box>
            <Box className="location-search-copy">
              <Typography variant="caption">ONDE VOCÊ ESTÁ?</Typography>
              <Typography fontWeight={900} noWrap>{cityLabel}</Typography>
            </Box>
            <TextField
              size="small"
              placeholder="Digite seu CEP"
              value={cep}
              onChange={(e) => setCep(e.target.value)}
              inputProps={{ inputMode: 'numeric', maxLength: 9, 'aria-label': 'Digite seu CEP' }}
            />
            <Button variant="contained" onClick={async () => { await applyCep(); setShowLocationEditor(false); }}>Usar</Button>
            {city && (
              <IconButton
                size="small"
                aria-label="Limpar localização"
                onClick={() => { setCity(''); setCityLabel('Todas as cidades'); setCep(''); }}
              >
                <Close />
              </IconButton>
            )}
          </Paper>
        )}

        {!lanchonetesOnly && activeOrder && (
          <Paper className="home-order-card active" onClick={() => go('orderDetail', { pedidoId: activeOrder.id })}>
            <Box className="home-order-icon"><ListAlt /></Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography className="home-order-eyebrow">PEDIDO EM ANDAMENTO</Typography>
              <Typography fontWeight={900} noWrap>{homeOrderStoreName(activeOrder)}</Typography>
              <Typography variant="body2" color="text.secondary">{statusText(activeOrder.status)}</Typography>
            </Box>
            <ArrowForward color="primary" />
          </Paper>
        )}

        {!lanchonetesOnly && !activeOrder && lastDeliveredOrder && (
          <Paper className="home-order-card previous" onClick={() => go('orderDetail', { pedidoId: lastDeliveredOrder.id })}>
            <Box className="home-order-icon"><ListAlt /></Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography className="home-order-eyebrow">SEU ÚLTIMO PEDIDO</Typography>
              <Typography fontWeight={900} noWrap>{homeOrderStoreName(lastDeliveredOrder)}</Typography>
              <Typography variant="body2" color="text.secondary">Ver detalhes do pedido</Typography>
            </Box>
            <ArrowForward color="primary" />
          </Paper>
        )}

        {lanchonetesOnly && (
          <TextField
            fullWidth
            placeholder="Buscar estabelecimento ou categoria..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />
        )}

        <Box className="category-strip">
          {[
            ['all', 'Todos', '✨'],
            ['restaurante', 'Restaurante', '🍽️'],
            ['pizzaria', 'Pizzaria', '🍕'],
            ['lanchonete', 'Lanches', '🍔'],
            ['sorveteria', 'Sorvetes', '🍦'],
            ['acai', 'Açaí', '🥤'],
            ['farmacia', 'Farmácia', '💊']
          ].map(([value, label, icon]) => (
            <Box
              key={value}
              className={`category-item${type === value ? ' active' : ''}`}
              onClick={() => setType(value)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => event.key === 'Enter' && setType(value)}
            >
              <span className="category-emoji">{icon}</span>
              <Typography variant="caption" fontWeight={900}>{label}</Typography>
            </Box>
          ))}
        </Box>

        {!lanchonetesOnly && banners.length > 0 && <Typography className="home-section-kicker">DESTAQUES PARA VOCÊ</Typography>}
        {!lanchonetesOnly && banners.length > 0 && (
          <Box className="banner-strip">
            {banners.map((banner, index) => (
              <img key={banner.id || index} src={assetUrl(banner.image)} alt="Banner promocional" />
            ))}
          </Box>
        )}

        <Box className="chip-scroll">
          <Chip icon={<Storefront />} label="Todas" color={filter === 'all' ? 'primary' : 'default'} onClick={() => setFilter('all')} />
          <Chip icon={<CheckCircle />} label="Abertas" color={filter === 'open' ? 'primary' : 'default'} onClick={() => setFilter('open')} />
          {lanchonetesOnly && <Chip label="Fechadas" color={filter === 'closed' ? 'primary' : 'default'} onClick={() => setFilter('closed')} />}
          <Chip icon={<Favorite />} label="Favoritas" color={filter === 'favorites' ? 'primary' : 'default'} onClick={() => setFilter('favorites')} />
        </Box>

        <Box className="section-title">
          <Box>
            <Typography variant="h5" fontWeight={900}>Peça perto de você</Typography>
            <Typography color="text.secondary" variant="body2">Estabelecimentos abertos aparecem primeiro</Typography>
          </Box>
          <Chip label={visibleStores.length} color="primary" />
        </Box>

        {loading ? <Box className="center"><CircularProgress /></Box> : visibleStores.length === 0 ? (
          <Paper className="home-empty-state">
            <Search sx={{ fontSize: 52 }} />
            <Typography variant="h6" fontWeight={900}>Nenhum estabelecimento encontrado</Typography>
            <Typography color="text.secondary">Limpe a busca ou escolha outra categoria.</Typography>
            <Button variant="contained" onClick={() => { setSearch(''); setFilter('all'); setType('all'); }}>Limpar filtros</Button>
          </Paper>
        ) : (
          <Box className="store-list">
            {visibleStores.map((item) => {
              const isAvailable = Boolean(item.is_open && item.is_online !== false);
              const features = item.customer_features || {};
              const storeBenefits = [
                features.loyalty_enabled === true ? 'Fidelidade' : null,
                item.free_delivery_active === true
                  ? (Number(item.free_delivery_threshold) > 0
                    ? `Frete grátis R$${Number(item.free_delivery_threshold).toFixed(0)}+`
                    : 'Frete grátis')
                  : null
              ].filter(Boolean).slice(0, 2);
              return (
              <Paper key={item.id} className={`store-card${isAvailable ? '' : ' closed'}`} onClick={() => go('store', { lanchoneteId: item.id })}>
                <Box className="store-image-wrap">
                  <Avatar className="store-avatar" src={assetUrl(item.image)} variant="rounded"><Storefront /></Avatar>
                  <span className={`availability-dot ${isAvailable ? 'open' : 'closed'}`} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box className="store-top">
                    <Typography className="store-name" fontWeight={900}>{item.name}</Typography>
                    <Chip size="small" label={isAvailable ? 'Aberto' : 'Fechado'} color={isAvailable ? 'success' : 'error'} />
                  </Box>
                  <Typography className="store-description" color="text.secondary">{item.description || 'Confira o cardapio e faca seu pedido'}</Typography>
                  <Box className="store-facts">
                    <Typography variant="caption" fontWeight={800}>
                      {Number(item.review_count) > 0 ? `★ ${Number(item.rating).toFixed(1)} (${item.review_count})` : 'Novo'}
                    </Typography>
                    {item.delivery_time && <Typography variant="caption">• {item.delivery_time} min</Typography>}
                    <Typography variant="caption" className="delivery-fee">• Entrega {money(deliveryFee(item))}</Typography>
                    {Number(item.minimum_order || item.min_order_value) > 0 && (
                      <Typography variant="caption">Pedido mín. {money(item.minimum_order || item.min_order_value)}</Typography>
                    )}
                  </Box>
                  {storeBenefits.length > 0 && (
                    <Box className="store-benefits">
                      {storeBenefits.map((benefit) => <Chip key={benefit} size="small" label={benefit} />)}
                    </Box>
                  )}
                </Box>
                <IconButton onClick={(e) => toggleFavorite(e, item.id)}>
                  {favorites[item.id] ? <Favorite color="primary" /> : <FavoriteBorder />}
                </IconButton>
              </Paper>
              );
            })}
          </Box>
        )}
      </Box>
      <Snackbar open={!!message} autoHideDuration={2500} onClose={() => setMessage('')} message={message} />
    </>
  );
}

function OptionSelector({ group, selected, onChange }) {
  const chosen = selected[group.id] || [];
  const max = Number(group.max_selections) || (group.type === 'single' ? 1 : 99);

  const toggle = (option) => {
    const exists = chosen.some((item) => item.id === option.id);
    let next;
    if (exists) {
      next = chosen.filter((item) => item.id !== option.id);
    } else if (max === 1) {
      next = [option];
    } else {
      next = chosen.length >= max ? chosen : [...chosen, option];
    }
    onChange(group.id, next);
  };

  return (
    <Paper className="option-card">
      <Box className="store-top">
        <Box>
          <Typography fontWeight={900}>{group.name}</Typography>
          <Typography color="text.secondary" variant="body2">
            {group.required ? `Obrigatorio: minimo ${group.min_selections || 1}` : 'Opcional'}
          </Typography>
        </Box>
        <Chip size="small" label={`${chosen.length}/${max}`} />
      </Box>
      {(group.options || []).map((option) => {
        const checked = chosen.some((item) => item.id === option.id);
        return (
          <Box key={option.id} className={`option-row ${checked ? 'active' : ''}`} onClick={() => toggle(option)}>
            <Box>
              <Typography fontWeight={800}>{option.name}</Typography>
              {option.description && <Typography variant="body2" color="text.secondary">{option.description}</Typography>}
            </Box>
            <Typography fontWeight={900}>{Number(option.price) > 0 ? money(option.price) : 'Gratis'}</Typography>
          </Box>
        );
      })}
    </Paper>
  );
}

function StoreView({ params, go }) {
  const { user } = useCustomerAuth();
  const { addToCart, clearCart, isFromSameLanchonete, items, subtotal } = useCart();
  const [lanchonete, setLanchonete] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [category, setCategory] = useState('todos');
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [product, setProduct] = useState(null);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState({});
  const [pizzaMode, setPizzaMode] = useState('whole');
  const [secondPizzaFlavor, setSecondPizzaFlavor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [observation, setObservation] = useState('');
  const [message, setMessage] = useState('');
  const lanchoneteId = params.lanchoneteId;

  useEffect(() => {
    async function load() {
      const [storeRes, productsRes] = await Promise.all([
        api.get(`/lanchonetes/${lanchoneteId}`),
        api.get(`/produtos?lanchonete_id=${lanchoneteId}&available=true`)
      ]);
      setLanchonete(storeRes.data);
      setProdutos(Array.isArray(productsRes.data) ? productsRes.data : []);
    }
    load();
  }, [lanchoneteId]);

  const openProduct = async (item) => {
    setProduct(item);
    setSelected({});
    setPizzaMode('whole');
    setSecondPizzaFlavor(null);
    setSelectedSize(getSizeOptions(item)[0] || null);
    setQuantity(1);
    setObservation('');
    const response = await api.get(`/product-options/groups/product/${item.id}`).catch(() => ({ data: [] }));
    setOptions(Array.isArray(response.data) ? response.data : []);
  };

  const validateOptions = () => {
    const normalizedType = `${product?.name || ''} ${product?.category || product?.categoria_name || ''}`
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const hasAvailableOptions = options.some((group) => Array.isArray(group.options) && group.options.length > 0);
    const needsAtLeastOneOption = hasAvailableOptions && (normalizedType.includes('acai') || normalizedType.includes('sorvete'));
    const selectedCount = Object.values(selected).reduce((total, list) => total + (Array.isArray(list) ? list.length : 0), 0);
    if (needsAtLeastOneOption && selectedCount === 0) {
      setMessage(`Selecione pelo menos uma opcao para ${product?.name || 'este produto'}.`);
      return false;
    }
    for (const group of options) {
      if (group.required && (selected[group.id] || []).length < Number(group.min_selections || 1)) {
        setMessage(`Selecione as opcoes obrigatorias em ${group.name}.`);
        return false;
      }
    }
    return true;
  };

  const confirmProduct = (addAnother = false) => {
    if (!product || !validateOptions()) return;
    if (getSizeOptions(product).length > 0 && !selectedSize) {
      setMessage('Escolha o tamanho da pizza.');
      return;
    }
    if (pizzaMode === 'half' && !secondPizzaFlavor) {
      setMessage('Escolha o segundo sabor da pizza.');
      return;
    }
    if (!isFromSameLanchonete(product.lanchonete_id)) {
      const shouldReplaceCart = window.confirm('Seu carrinho tem itens de outra lanchonete. Deseja limpar o carrinho e adicionar este item?');
      if (!shouldReplaceCart) return;
      clearCart();
    }
    const customizations = Object.keys(selected)
      .map((groupId) => {
        const group = options.find((item) => Number(item.id) === Number(groupId));
        return { groupId: Number(groupId), groupName: group?.name || 'Adicional', selectedOptions: selected[groupId] || [] };
      })
      .filter((item) => item.selectedOptions.length > 0);
    const sizeCustomization = selectedSize ? [{
      groupId: 'pizza-size',
      groupName: 'Tamanho',
      selectedOptions: [{ id: `pizza-${selectedSize.label}`, name: `Pizza ${selectedSize.label}`, price: 0 }]
    }] : [];
    const pizzaCustomizations = pizzaMode === 'half'
      ? [{
          groupId: 'pizza-flavors',
          groupName: 'Sabores (metade/metade)',
          selectedOptions: [
            { id: product.id, name: `Metade ${product.name}`, price: 0 },
            { id: secondPizzaFlavor.id, name: `Metade ${secondPizzaFlavor.name}`, price: 0 }
          ]
        }, ...sizeCustomization, ...customizations]
      : [...sizeCustomization, ...customizations];
    const secondFlavorSize = selectedSize
      ? getSizeOptions(secondPizzaFlavor).find((size) => size.label === selectedSize.label)
      : null;
    const basePrice = pizzaMode === 'half'
      ? Math.max(getProductPrice(product, selectedSize), getProductPrice(secondPizzaFlavor, secondFlavorSize))
      : getProductPrice(product, selectedSize);
    const productForCart = {
      ...product,
      name: pizzaMode === 'half' ? `Pizza metade/metade - ${product.name} / ${secondPizzaFlavor.name}` : product.name,
      price: basePrice,
      size_label: selectedSize?.label || null
    };
    addToCart(productForCart, addAnother ? 1 : quantity, pizzaCustomizations, observation.trim());
    if (addAnother) {
      setSelected({});
      setObservation('');
      setQuantity(1);
      setMessage('Unidade adicionada. Escolha as opcoes da proxima.');
      return;
    }
    setProduct(null);
    setMessage('Produto adicionado ao carrinho');
  };

  const categories = ['todos', ...new Set(produtos.map((item) => item.category).filter(Boolean))];
  const normalizedProductCategory = String(product?.category || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const isPizzaProduct = String(lanchonete?.type || '').toLowerCase() === 'pizzaria' && normalizedProductCategory.includes('pizza');
  const secondFlavorOptions = isPizzaProduct
    ? produtos.filter((item) => item.id !== product?.id && item.available !== false && String(item.category || '') === String(product?.category || ''))
    : [];
  const selectedSecondFlavorSize = selectedSize
    ? getSizeOptions(secondPizzaFlavor).find((size) => size.label === selectedSize.label)
    : null;
  const displayedProductPrice = pizzaMode === 'half' && secondPizzaFlavor
    ? Math.max(getProductPrice(product, selectedSize), getProductPrice(secondPizzaFlavor, selectedSecondFlavorSize))
    : getProductPrice(product, selectedSize);
  const normalizedSearch = productSearch.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const filtered = produtos.filter((item) => {
    const matchesCategory = category === 'todos' || item.category === category;
    const searchableText = `${item.name || ''} ${item.description || ''}`
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return matchesCategory && (!normalizedSearch || searchableText.includes(normalizedSearch));
  });
  const storeItems = items.filter((item) => Number(item.lanchonete_id) === Number(lanchoneteId));
  const storeCount = storeItems.reduce((sum, item) => sum + item.quantity, 0);
  const manualDeliveryFee = deliveryMode(lanchonete) === 'APP'
    ? APP_DELIVERY_FEE
    : Number(lanchonete?.delivery_fee) || 0;
  const hasManualDeliveryFee = storeItems.some((item) => item.is_manual_delivery_fee);
  const canAddManualDeliveryFee = user?.user_type === 'waiter' && params.mesaId === 'livre' && manualDeliveryFee > 0;

  const addManualDeliveryFee = () => {
    if (hasManualDeliveryFee) {
      setMessage('A taxa de entrega ja foi adicionada.');
      return;
    }
    addToCart({
      id: MANUAL_DELIVERY_FEE_ID,
      name: 'Taxa de entrega',
      description: 'Taxa adicionada manualmente pelo garcom',
      price: manualDeliveryFee,
      lanchonete_id: Number(lanchoneteId),
      is_manual_delivery_fee: true
    }, 1);
    setMessage('Taxa de entrega adicionada ao pedido');
  };

  if (!lanchonete) return <Box className="center"><CircularProgress /></Box>;

  return (
    <Box className={`store-screen${storeCount > 0 ? ' has-cart' : ''}${lanchonete.is_open && lanchonete.is_online !== false ? '' : ' closed-store'}`}>
      <Box className="store-hero">
        <img src={assetUrl(lanchonete.image)} alt={lanchonete.name} />
        <Box className="hero-actions">
          <IconButton onClick={() => go('home')}><ArrowBack /></IconButton>
          <Chip label={lanchonete.is_open ? 'Aberto' : 'Fechado'} color={lanchonete.is_open ? 'success' : 'error'} />
        </Box>
      </Box>
      <Box className="store-info">
        <Avatar src={assetUrl(lanchonete.image)} className="store-logo"><Storefront /></Avatar>
        <Typography variant="h5" fontWeight={900}>{lanchonete.name}</Typography>
        <Typography color="text.secondary">{lanchonete.description || 'A melhor lanchonete da regiao'}</Typography>
        <Box className="store-metas">
          {(lanchonete.delivery_time || lanchonete.opening_hours) && (
            <Chip label={lanchonete.delivery_time ? `${lanchonete.delivery_time} min` : lanchonete.opening_hours} />
          )}
          <Chip label={`Taxa ${money(deliveryFee(lanchonete))}`} color="success" />
        </Box>
      </Box>
      <Box className="screen-body">
        {canAddManualDeliveryFee && (
          <Paper
            className={`manual-delivery-fee-card${hasManualDeliveryFee ? ' added' : ''}`}
            onClick={addManualDeliveryFee}
          >
            <LocalShipping />
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={900}>Taxa de entrega</Typography>
              <Typography color="text.secondary" variant="body2">
                Adicione quando o pedido de entrega foi feito por telefone, WhatsApp ou balcao.
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography fontWeight={900}>{money(manualDeliveryFee)}</Typography>
              <Chip size="small" color={hasManualDeliveryFee ? 'success' : 'primary'} label={hasManualDeliveryFee ? 'Adicionada' : 'Adicionar'} />
            </Box>
          </Paper>
        )}
        {user?.user_type === 'waiter' && (
          <TextField
            fullWidth
            value={productSearch}
            onChange={(event) => setProductSearch(event.target.value)}
            placeholder="Pesquisar produto pelo nome..."
            inputProps={{ 'aria-label': 'Pesquisar produto' }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
              endAdornment: productSearch ? (
                <InputAdornment position="end">
                  <IconButton aria-label="Limpar pesquisa" onClick={() => setProductSearch('')}><Close /></IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, backgroundColor: '#fff' } }}
          />
        )}
        <Box className="section-title">
          <Typography variant="h5" fontWeight={900}>Destaques</Typography>
          <Chip label={filtered.length} color="primary" />
        </Box>
        <Box className="product-list">
          {filtered.map((item) => (
            <Paper key={item.id} className="product-card" onClick={() => openProduct(item)}>
              <img src={assetUrl(item.image)} alt={item.name} />
              <Box className="product-info">
                <Box className="store-top">
                  <Typography fontWeight={900}>{productPriceLabel(item)}</Typography>
                  <IconButton size="small" className="add-round" onClick={(e) => { e.stopPropagation(); openProduct(item); }}><Add /></IconButton>
                </Box>
                <Typography fontWeight={900}>{item.name}</Typography>
                {item.free_delivery === true && <Chip size="small" color="success" label="Frete grátis" sx={{ alignSelf: 'flex-start' }} />}
                <Typography color="text.secondary" variant="body2">{item.description || 'Produto feito com ingredientes selecionados'}</Typography>
              </Box>
            </Paper>
          ))}
          {filtered.length === 0 && (
            <Paper className="home-empty-state">
              <Search sx={{ fontSize: 44 }} />
              <Typography fontWeight={900}>Nenhum produto encontrado</Typography>
              <Button onClick={() => { setProductSearch(''); setCategory('todos'); }}>Limpar pesquisa</Button>
            </Paper>
          )}
        </Box>
      </Box>
      {storeCount > 0 && (
        <Button className="cart-bar" variant="contained" onClick={() => go('cart', { mesaId: params.mesaId, lanchoneteId })}>
          {storeCount} item(ns) - {money(subtotal)} <span>{params.mesaId ? 'Lancar na Mesa' : 'Ver carrinho'}</span>
        </Button>
      )}
      <Button
        className={`categories-fab${storeCount > 0 ? ' with-cart' : ''}`}
        variant="contained"
        startIcon={<ListAlt />}
        onClick={() => setCategoryDialogOpen(true)}
      >
        Categorias
      </Button>
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Categorias</DialogTitle>
        <DialogContent>
          <Box className="category-modal-list">
            {categories.map((item) => (
              <Button
                key={item}
                variant={category === item ? 'contained' : 'text'}
                onClick={() => {
                  setCategory(item);
                  setCategoryDialogOpen(false);
                }}
              >
                {item === 'todos' ? 'Todos' : item}
              </Button>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog open={!!product} onClose={() => setProduct(null)} fullWidth maxWidth="sm">
        {product && (
          <>
            <DialogTitle sx={{ p: 0 }}>
              <Box className="product-modal-hero">
                <img src={assetUrl(product.image)} alt={product.name} />
                <IconButton onClick={() => setProduct(null)}><Close /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent className="product-modal-content">
              <Typography variant="h5" fontWeight={900}>{product.name}</Typography>
              <Typography color="text.secondary">{product.description}</Typography>
              <Typography variant="h5" color="primary" fontWeight={900} sx={{ mt: 1 }}>{money(displayedProductPrice)}</Typography>
              {getSizeOptions(product).length > 0 && (
                <Paper className="option-card" sx={{ mt: 2 }}>
                  <Typography fontWeight={900}>Escolha o tamanho</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${getSizeOptions(product).length}, 1fr)`, gap: 1, mt: 1 }}>
                    {getSizeOptions(product).map((size) => (
                      <Button key={size.label} variant={selectedSize?.label === size.label ? 'contained' : 'outlined'} onClick={() => setSelectedSize(size)}>
                        {size.label} - {money(size.price)}
                      </Button>
                    ))}
                  </Box>
                </Paper>
              )}
              {isPizzaProduct && secondFlavorOptions.length > 0 && (
                <Paper className="pizza-half-card">
                  <Typography fontWeight={900}>Como deseja sua pizza?</Typography>
                  <Box className="pizza-mode-buttons">
                    <Button variant={pizzaMode === 'whole' ? 'contained' : 'outlined'} onClick={() => { setPizzaMode('whole'); setSecondPizzaFlavor(null); }}>
                      Sabor inteiro
                    </Button>
                    <Button variant={pizzaMode === 'half' ? 'contained' : 'outlined'} onClick={() => setPizzaMode('half')}>
                      Metade/metade
                    </Button>
                  </Box>
                  {pizzaMode === 'half' && (
                    <Box className="pizza-flavor-list">
                      <Typography variant="body2" color="text.secondary">
                        Primeiro sabor: <strong>{product.name}</strong>. Escolha o segundo sabor; sera cobrado o mais caro.
                      </Typography>
                      {secondFlavorOptions.map((flavor) => (
                        <Box
                          key={flavor.id}
                          className={`pizza-flavor-option ${secondPizzaFlavor?.id === flavor.id ? 'active' : ''}`}
                          onClick={() => setSecondPizzaFlavor(flavor)}
                        >
                          <Box>
                            <Typography fontWeight={800}>{flavor.name}</Typography>
                            {flavor.description && <Typography variant="body2" color="text.secondary">{flavor.description}</Typography>}
                          </Box>
                          <Typography fontWeight={900}>{money(flavor.price)}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              )}
              <Box className="quantity-row">
                <Typography fontWeight={900}>Quantidade</Typography>
                <Box>
                  <IconButton onClick={() => setQuantity(Math.max(1, quantity - 1))}><Remove /></IconButton>
                  <strong>{quantity}</strong>
                  <IconButton onClick={() => setQuantity(quantity + 1)}><Add /></IconButton>
                </Box>
              </Box>
              {options.map((group) => (
                <OptionSelector
                  key={group.id}
                  group={group}
                  selected={selected}
                  onChange={(groupId, next) => setSelected((prev) => ({ ...prev, [groupId]: next }))}
                />
              ))}
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Observacoes"
                placeholder="Ex: sem cebola, ponto bem passado..."
                value={observation}
                onChange={(e) => setObservation(e.target.value.slice(0, 200))}
              />
            </DialogContent>
            <DialogActions>
              {options.length > 0 && (
                <Button fullWidth variant="outlined" size="large" onClick={() => confirmProduct(true)}>
                  Adicionar este e escolher outro
                </Button>
              )}
              <Button fullWidth variant="contained" size="large" onClick={() => confirmProduct(false)}>Adicionar ao carrinho</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Snackbar open={!!message} autoHideDuration={2500} onClose={() => setMessage('')} message={message} />
    </Box>
  );
}

function CartView({ go, params = {} }) {
  const { user, guestMode } = useCustomerAuth();
  const { items, updateQuantity, clearCart, lanchoneteId, calculateUnitPrice } = useCart();
  const [lanchonete, setLanchonete] = useState(null);
  const [profile, setProfile] = useState(null);
  const [coupon, setCoupon] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [payment, setPayment] = useState('');
  const [needsChange, setNeedsChange] = useState(false);
  const [changeAmount, setChangeAmount] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [alternateAddress, setAlternateAddress] = useState('');
  const [alternateHouseNumber, setAlternateHouseNumber] = useState('');
  const [alternateReference, setAlternateReference] = useState('');
  const [guestData, setGuestData] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}:guestData`) || '{"name":"","phone":"","email":""}');
    } catch (_) {
      return { name: '', phone: '', email: '' };
    }
  });
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [deliveryQuoteLoading, setDeliveryQuoteLoading] = useState(false);
  const [deliveryQuoteError, setDeliveryQuoteError] = useState(false);
  const [locating, setLocating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pix, setPix] = useState(null);
  const [message, setMessage] = useState('');
  const [hasFreeDeliveryBenefit, setHasFreeDeliveryBenefit] = useState(false);
  const checkoutInFlightRef = useRef(false);
  const pendingOrderKeyRef = useRef(null);
  const mesaId = params.mesaId;
  const activeLanchoneteId = params.lanchoneteId || lanchoneteId;
  const activeItems = activeLanchoneteId
    ? items.filter((item) => Number(item.lanchonete_id) === Number(activeLanchoneteId))
    : items;
  const activeSubtotal = activeItems.reduce((sum, item) => sum + calculateUnitPrice(item) * item.quantity, 0);
  const manualDeliveryFeeItems = activeItems.filter((item) => item.is_manual_delivery_fee);
  const orderItems = activeItems.filter((item) => !item.is_manual_delivery_fee);
  const hasProductFreeDelivery = orderItems.some((item) => item.free_delivery === true || item.free_delivery === 'true');
  const manualDeliveryFee = manualDeliveryFeeItems.reduce(
    (sum, item) => sum + calculateUnitPrice(item) * item.quantity,
    0
  );
  const deliveryLatitude = deliveryLocation?.latitude;
  const deliveryLongitude = deliveryLocation?.longitude;
  const currentDeliveryMode = String(lanchonete?.delivery_mode || 'LOJA').toUpperCase();

  useEffect(() => {
    if (!activeLanchoneteId || deliveryLatitude == null || deliveryLongitude == null || currentDeliveryMode !== 'APP') {
      setDeliveryQuote(null);
      setDeliveryQuoteLoading(false);
      setDeliveryQuoteError(false);
      return undefined;
    }
    let active = true;
    setDeliveryQuoteLoading(true);
    setDeliveryQuoteError(false);
    api.get('/delivery-pricing/quote', {
      params: {
        lanchonete_id: activeLanchoneteId,
        latitude: deliveryLatitude,
        longitude: deliveryLongitude
      }
    }).then(({ data }) => {
      if (active) setDeliveryQuote(data);
    }).catch(() => {
      if (active) {
        setDeliveryQuote(null);
        setDeliveryQuoteError(true);
      }
    }).finally(() => {
      if (active) setDeliveryQuoteLoading(false);
    });
    return () => { active = false; };
  }, [activeLanchoneteId, deliveryLatitude, deliveryLongitude, currentDeliveryMode]);
  const returnToCatalog = () => {
    if (activeLanchoneteId) {
      go('store', {
        lanchoneteId: activeLanchoneteId,
        ...(mesaId ? { mesaId } : {})
      });
      return;
    }
    go(user?.user_type === 'waiter' ? 'waiter' : 'home');
  };

  useEffect(() => {
    async function load() {
      if (activeLanchoneteId) {
        const storeRes = await api.get(`/lanchonetes/${activeLanchoneteId}`);
        setLanchonete(storeRes.data);
      }
      const profileRes = guestMode ? { data: null } : await api.get('/users/profile').catch(() => ({ data: user }));
      setProfile(profileRes.data);
      const benefitRes = guestMode ? { data: {} } : await api.get('/promocoes/active-benefit').catch(() => ({ data: {} }));
      setHasFreeDeliveryBenefit(!!benefitRes.data?.hasFreeDelivery);
    }
    load();
  }, [activeLanchoneteId, user, guestMode]);

  useEffect(() => {
    if (!pix?.pedidoId) return undefined;
    const timer = setInterval(async () => {
      try {
        const guestToken = localStorage.getItem(`${STORAGE_PREFIX}:guestOrder:${pix.pedidoId}`);
        const response = guestToken
          ? await api.get(`/pedidos/guest/${pix.pedidoId}`, { headers: { 'X-Guest-Order-Token': guestToken } })
          : await api.get(`/pedidos/${pix.pedidoId}`);
        if (!orderNeedsPayment(response.data)) {
          const paidOrderId = pix.pedidoId;
          clearCart();
          setPix(null);
          go('orderDetail', { pedidoId: paidOrderId });
        }
      } catch (_) {
        // O PIX continua visível enquanto não houver confirmação segura.
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [pix?.pedidoId, clearCart, go]);

  const quotedDeliveryFee = deliveryMode(lanchonete) === 'APP'
    ? Number(deliveryQuote?.delivery_fee ?? APP_DELIVERY_FEE)
    : deliveryFee(lanchonete, payment);
  const baseFee = hasFreeDeliveryBenefit || hasProductFreeDelivery ? 0 : quotedDeliveryFee;
  const fee = mesaId || payment === 'pickup' ? 0 : appliedCoupon ? Number(appliedCoupon.deliveryFee) || 0 : baseFee;
  const discount = appliedCoupon ? Number(appliedCoupon.discount) || 0 : 0;
  const calculationSubtotal = activeSubtotal - (hasProductFreeDelivery ? manualDeliveryFee : 0);
  const total = appliedCoupon ? Number(appliedCoupon.total) || Math.max(0, calculationSubtotal + fee - discount) : calculationSubtotal + fee;

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const response = await api.post('/promocoes/validate', {
        code: coupon.trim(),
        lanchonete_id: activeLanchoneteId,
        subtotal: activeSubtotal,
        delivery_fee: baseFee
      });
      if (response.data.valid) {
        setAppliedCoupon(response.data);
        setMessage('Cupom aplicado com sucesso');
      } else {
        setMessage(response.data.error || 'Cupom invalido');
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Cupom invalido');
    }
  };

  const captureDeliveryLocation = () => {
    if (!navigator.geolocation) {
      setMessage('Este navegador nao oferece acesso a localizacao.');
      return;
    }

    setDeliveryLocation(null);
    setLocating(true);

    let bestPosition = null;
    let settled = false;
    let watchId;
    const stop = () => {
      if (watchId !== undefined) navigator.geolocation.clearWatch(watchId);
      window.clearTimeout(captureTimeout);
    };
    const acceptBestPosition = () => {
      if (settled) return;
      settled = true;
      stop();
      setLocating(false);

      if (!bestPosition || !Number.isFinite(bestPosition.coords.accuracy) || bestPosition.coords.accuracy > 100) {
        setMessage('O GPS nao conseguiu uma posicao precisa. Ative a localizacao exata do celular, va para um local aberto e tente novamente.');
        return;
      }

      const { latitude, longitude, accuracy } = bestPosition.coords;
      setDeliveryLocation({ latitude, longitude, accuracy });
      setMessage('Localizacao exata marcada. Confira o ponto no mapa antes de finalizar.');
    };
    const captureTimeout = window.setTimeout(acceptBestPosition, 20000);

    watchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!bestPosition || position.coords.accuracy < bestPosition.coords.accuracy) {
          bestPosition = position;
        }
        // Uma leitura com erro de ate 40 m ja e suficientemente precisa para entrega.
        if (position.coords.accuracy <= 40) acceptBestPosition();
      },
      (error) => {
        if (settled) return;
        settled = true;
        stop();
        setLocating(false);
        setMessage(error.code === error.PERMISSION_DENIED
          ? 'Permissao negada. No celular, permita a localizacao precisa para este site.'
          : 'Nao foi possivel obter uma localizacao precisa. Verifique o GPS e tente novamente.');
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  const previewDeliveryLocation = () => {
    if (!deliveryLocation) return;
    const point = `${deliveryLocation.latitude},${deliveryLocation.longitude}`;
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(point)}`, '_blank', 'noopener,noreferrer');
  };

  const checkout = async () => {
    // Impede que um toque duplo crie dois pedidos de mesa antes de a tela atualizar.
    if (checkoutInFlightRef.current) return;
    if (!orderItems.length) {
      setMessage('Adicione itens desta lanchonete antes de finalizar.');
      return;
    }
    const isFreeTable = mesaId === 'livre';
    if ((!mesaId || isFreeTable) && !payment) {
      setPaymentDialogOpen(true);
      return;
    }
    if (payment === 'money' && needsChange && Number(String(changeAmount).replace(',', '.')) <= 0) {
      setMessage('Informe o valor para o qual o entregador deve levar troco.');
      return;
    }
    if (guestMode && (!guestData.name.trim() || !guestData.phone.trim())) {
      setMessage('Informe seu nome e WhatsApp para finalizar sem criar conta.');
      return;
    }
    if (!mesaId && payment !== 'pickup' && deliveryMode(lanchonete) === 'APP' && deliveryLocation && (deliveryQuoteLoading || deliveryQuoteError || !deliveryQuote)) {
      setMessage(deliveryQuoteError ? 'Não foi possível confirmar o frete por KM. Tente novamente antes de finalizar.' : 'Aguarde o cálculo do frete por KM antes de finalizar.');
      return;
    }
    const address = isFreeTable
      ? alternateAddress.trim() || 'Mesa Livre'
      : mesaId ? `Mesa ${mesaId}` : payment === 'pickup' ? 'Retirada no local' : alternateAddress.trim() || profile?.address || user?.address || '';
    const houseNumber = isFreeTable
      ? alternateHouseNumber.trim()
      : mesaId || payment === 'pickup'
      ? ''
      : alternateAddress.trim()
        ? alternateHouseNumber.trim()
        : alternateHouseNumber.trim() || profile?.house_number || user?.house_number || '';
    const orderReference = isFreeTable
      ? alternateReference.trim()
      : alternateReference.trim() || profile?.reference || user?.reference || '';
    const customerPhone = guestMode ? guestData.phone.trim() : profile?.phone || user?.phone;
    if (!mesaId && payment !== 'pickup' && (!customerPhone || !address || !houseNumber || !(alternateReference.trim() || profile?.reference || user?.reference))) {
      setMessage('Complete telefone, endereco, numero da casa e referencia no perfil ou no carrinho.');
      return;
    }
    checkoutInFlightRef.current = true;
    setLoading(true);
    try {
      const pedidoData = {
        lanchonete_id: activeLanchoneteId,
        customer_name: guestMode ? guestData.name.trim() : profile?.name || user?.name || 'Cliente',
        customer_email: guestMode ? guestData.email.trim() || undefined : profile?.email || user?.email,
        customer_phone: customerPhone,
        customer_cpf: profile?.cpf || user?.cpf,
        delivery_address: address,
        delivery_latitude: !mesaId && payment !== 'pickup' ? deliveryLocation?.latitude ?? null : null,
        delivery_longitude: !mesaId && payment !== 'pickup' ? deliveryLocation?.longitude ?? null : null,
        house_number: houseNumber,
        reference: orderReference,
        mesa_id: mesaId || null,
        payment_method: mesaId && !isFreeTable ? 'mesa' : payment,
        needs_change: payment === 'money' && needsChange,
        change_amount: payment === 'money' && needsChange
          ? Number(String(changeAmount).replace(',', '.')) || 0
          : 0,
        delivery_fee: manualDeliveryFee || fee,
        tipo_entrega: manualDeliveryFee > 0
          ? deliveryMode(lanchonete)
          : mesaId ? 'MESA' : payment === 'pickup' ? 'RETIRADA' : deliveryMode(lanchonete),
        origem: 'WEB_PWA',
        coupon_code: appliedCoupon ? appliedCoupon.code : null,
        total,
        device_id: localStorage.getItem(`${STORAGE_PREFIX}:mp_device_id`) || `web-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        items: orderItems.map((item) => ({
          produto_id: item.produto_id,
          quantity: item.quantity,
          price: calculateUnitPrice(item),
          size_label: item.size_label || null,
          observation: item.observation || '',
          observations: item.observation || '',
          customizations: item.customizations || []
        }))
      };
      localStorage.setItem(`${STORAGE_PREFIX}:mp_device_id`, pedidoData.device_id);
      const fingerprint = await checkoutFingerprint({
        ...pedidoData,
        checkout_identity: guestMode ? 'guest' : `user-${user?.id || 'unknown'}`
      });
      let storedPendingOrder = null;
      try {
        storedPendingOrder = JSON.parse(localStorage.getItem(PENDING_ORDER_KEY) || 'null');
      } catch (_) {
        storedPendingOrder = null;
      }
      const cachedPendingOrder = pendingOrderKeyRef.current?.fingerprint === fingerprint
        ? pendingOrderKeyRef.current
        : storedPendingOrder?.fingerprint === fingerprint ? storedPendingOrder : null;
      const idempotencyKey = cachedPendingOrder?.key
        || `web-order-${user?.id || 'guest'}-${mesaId || 'delivery'}-${secureIdempotencyId()}`;
      const guestReplayToken = guestMode
        ? cachedPendingOrder?.guestReplayToken || secureIdempotencyId()
        : null;
      pendingOrderKeyRef.current = { fingerprint, key: idempotencyKey, guestReplayToken };
      localStorage.setItem(PENDING_ORDER_KEY, JSON.stringify(pendingOrderKeyRef.current));
      const checkoutHeaders = {
        'Idempotency-Key': idempotencyKey,
        ...(guestReplayToken ? { 'X-Guest-Order-Token': guestReplayToken } : {})
      };
      const response = mesaId
        ? await api.post(`/mesas/${mesaId}/pedido`, pedidoData, {
            headers: checkoutHeaders
          })
        : await api.post('/pedidos', pedidoData, {
            headers: checkoutHeaders
          });
      const data = response.data;
      const pedidoId = data?.pedido?.id || data?.id;
      if (pedidoId && data.guestAccessToken) {
        localStorage.setItem(`${STORAGE_PREFIX}:guestOrder:${pedidoId}`, data.guestAccessToken);
        let guestOrderIds = [];
        try {
          guestOrderIds = JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}:guestOrders`) || '[]');
        } catch (_) {
          guestOrderIds = [];
        }
        localStorage.setItem(
          `${STORAGE_PREFIX}:guestOrders`,
          JSON.stringify([pedidoId, ...guestOrderIds.filter((id) => Number(id) !== Number(pedidoId))].slice(0, 30))
        );
        localStorage.setItem(`${STORAGE_PREFIX}:guestData`, JSON.stringify(guestData));
      }
      pendingOrderKeyRef.current = null;
      localStorage.removeItem(PENDING_ORDER_KEY);
      const paymentLink = data.paymentLink || data?.pedido?.payment_link;
      const pixCode = data?.pixData?.qr_code || data?.pedido?.pix_qr_code || data?.pix_qr_code || '';
      const pixBase64 = data?.pixData?.qr_code_base64 || data?.pedido?.pix_qr_code_base64 || data?.pix_qr_code_base64 || '';
      if (mesaId) {
        clearCart();
        setMessage('Pedido enviado para a cozinha');
        go('waiter');
      } else if (payment === 'card' && paymentLink) {
        clearCart();
        window.open(paymentLink, '_blank', 'noopener,noreferrer');
        go('orderDetail', { pedidoId });
      } else if (payment === 'pix' && (pixCode || pixBase64 || paymentLink)) {
        setPix({ pedidoId, code: pixCode, qr: pixBase64, link: paymentLink });
      } else {
        clearCart();
        go('orderDetail', { pedidoId });
      }
    } catch (error) {
      setMessage(error.response?.data?.details || error.response?.data?.error || 'Nao foi possivel confirmar o pedido');
    } finally {
      setLoading(false);
      checkoutInFlightRef.current = false;
    }
  };

  if (!activeItems.length) {
    return (
      <Box className="empty-state">
        <ShoppingCart sx={{ fontSize: 60 }} />
        <Typography variant="h5" fontWeight={900}>Carrinho vazio</Typography>
        <Button variant="contained" onClick={returnToCatalog}>
          {activeLanchoneteId ? 'Voltar ao cardápio' : 'Ver lanchonetes'}
        </Button>
      </Box>
    );
  }

  return (
    <>
      <TopBar title="Carrinho" onBack={returnToCatalog} action={<IconButton onClick={clearCart}><Delete /></IconButton>} />
      <Box className="screen-body">
        {guestMode && !mesaId && (
          <Paper className="checkout-card">
            <Typography fontWeight={900}>Seus dados para o pedido</Typography>
            <Alert severity="info">Nao precisa criar senha. Esses dados serao usados somente no pedido e na entrega.</Alert>
            <TextField fullWidth required label="Seu nome" value={guestData.name} onChange={(e) => setGuestData({ ...guestData, name: e.target.value })} />
            <TextField fullWidth required label="WhatsApp" inputProps={{ inputMode: 'tel' }} value={guestData.phone} onChange={(e) => setGuestData({ ...guestData, phone: e.target.value })} />
            <TextField fullWidth type="email" label="E-mail (opcional)" value={guestData.email} onChange={(e) => setGuestData({ ...guestData, email: e.target.value })} />
          </Paper>
        )}
        <Box className="cart-items">
          {activeItems.map((item) => (
            <Paper key={item.key} className="cart-item">
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={900}>{item.name}</Typography>
                <Typography color="primary" fontWeight={900}>{money(calculateUnitPrice(item))}</Typography>
                {item.observation && <Typography variant="body2" color="text.secondary">Obs: {item.observation}</Typography>}
                {(item.customizations || []).map((group) => (
                  <Typography key={group.groupId} variant="body2" color="text.secondary">
                    {group.groupName}: {(group.selectedOptions || []).map((option) => option.name).join(', ')}
                  </Typography>
                ))}
              </Box>
              {item.is_manual_delivery_fee ? (
                <IconButton aria-label="Remover taxa de entrega" onClick={() => updateQuantity(item.key, 0)}><Delete /></IconButton>
              ) : (
                <Box className="qty-mini">
                  <IconButton onClick={() => updateQuantity(item.key, item.quantity - 1)}><Remove /></IconButton>
                  <strong>{item.quantity}</strong>
                  <IconButton onClick={() => updateQuantity(item.key, item.quantity + 1)}><Add /></IconButton>
                </Box>
              )}
            </Paper>
          ))}
        </Box>

        <Paper className="checkout-card">
          <Typography fontWeight={900}>Entrega</Typography>
          <Typography color="text.secondary">{alternateAddress || profile?.address || user?.address || 'Endereco nao informado'}</Typography>
          <Typography color="text.secondary">Numero: {alternateHouseNumber || profile?.house_number || user?.house_number || 'Nao informado'}</Typography>
          <TextField sx={{ mt: 2 }} fullWidth label="Endereço" value={alternateAddress} onChange={(e) => { setAlternateAddress(e.target.value); setDeliveryLocation(null); }} />
          <TextField sx={{ mt: 2 }} fullWidth label="Numero da casa" value={alternateHouseNumber} onChange={(e) => setAlternateHouseNumber(e.target.value)} />
          <TextField sx={{ mt: 2 }} fullWidth label="Referencia" value={alternateReference} onChange={(e) => setAlternateReference(e.target.value)} />
          <Button variant={deliveryLocation ? 'contained' : 'outlined'} startIcon={locating ? <CircularProgress size={18} color="inherit" /> : <MyLocation />} disabled={locating} onClick={captureDeliveryLocation}>
            {locating ? 'Buscando localizacao...' : deliveryLocation ? 'Localizacao exata marcada' : 'Usar minha localizacao exata'}
          </Button>
          {deliveryLocation && (
            <>
              <Alert severity="success">
                Ponto de entrega salvo (precisao aproximada de {Math.round(deliveryLocation.accuracy)} m). Confira se o mapa mostra sua casa.
              </Alert>
              <Button variant="outlined" startIcon={<LocationOn />} onClick={previewDeliveryLocation}>Conferir ponto no mapa</Button>
            </>
          )}
        </Paper>

        <Paper className="checkout-card">
          <Typography fontWeight={900}>Cupom</Typography>
          <Box className="coupon-row">
            <TextField fullWidth label="Codigo do cupom" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} disabled={!!appliedCoupon} />
            {appliedCoupon ? <Button color="error" onClick={() => { setAppliedCoupon(null); setCoupon(''); }}>Remover</Button> : <Button variant="contained" onClick={applyCoupon}>Aplicar</Button>}
          </Box>
          {appliedCoupon && <Alert severity="success" sx={{ mt: 2 }}>Cupom {appliedCoupon.code} aplicado.</Alert>}
        </Paper>

        <Paper className="checkout-card">
          <Typography fontWeight={900}>{mesaId && mesaId !== 'livre' ? `Pedido da Mesa ${mesaId}` : 'Forma de pagamento'}</Typography>
          {mesaId && mesaId !== 'livre' ? (
            <Alert severity="info">Este pedido sera lancado direto para a cozinha.</Alert>
          ) : (
            <>
              {mesaId === 'livre' ? (
                <Box className="payment-modal-grid" sx={{ mt: 1 }}>
                  {[
                    ['pix', 'Pix'],
                    ['card', 'Cartao'],
                    ['money', 'Dinheiro'],
                    ['local', 'Comer no local'],
                    ['pickup', 'Retirada no local']
                  ].map(([value, label]) => (
                    <Button
                      key={value}
                      variant={payment === value ? 'contained' : 'outlined'}
                      size="large"
                      onClick={() => {
                        setPayment(value);
                        if (value === 'pickup') {
                          const deliveryItem = items.find((item) => item.is_manual_delivery_fee);
                          if (deliveryItem) updateQuantity(deliveryItem.key, 0);
                        }
                        if (value !== 'money') {
                          setNeedsChange(false);
                          setChangeAmount('');
                        }
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </Box>
              ) : (
                <Button variant="outlined" size="large" onClick={() => setPaymentDialogOpen(true)}>
                  {payment ? { pix: 'Pix', card: 'Cartao', money: 'Dinheiro', pickup: 'Retirada no local', local: 'Comer no local' }[payment] : 'Escolher forma de pagamento'}
                </Button>
              )}
              {payment === 'money' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight={700} gutterBottom>Precisa de troco?</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant={needsChange ? 'contained' : 'outlined'} onClick={() => setNeedsChange(true)}>Sim</Button>
                    <Button variant={!needsChange ? 'contained' : 'outlined'} onClick={() => { setNeedsChange(false); setChangeAmount(''); }}>Nao</Button>
                  </Box>
                  {needsChange && (
                    <TextField
                      sx={{ mt: 2 }}
                      fullWidth
                      label="Troco para quanto?"
                      placeholder="Ex: 50,00"
                      value={changeAmount}
                      onChange={(e) => setChangeAmount(e.target.value)}
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </Paper>

        <Paper className="checkout-card">
          {(hasFreeDeliveryBenefit || hasProductFreeDelivery) && !mesaId && payment !== 'pickup' && (
            <Alert severity="success" className="benefit-alert">
              {hasProductFreeDelivery ? 'Este pedido tem frete gratis por causa de um produto selecionado!' : 'Voce tem Frete Gratis da plataforma!'}
            </Alert>
          )}
          <Box className="summary-row"><span>Subtotal</span><strong>{money(activeSubtotal - manualDeliveryFee)}</strong></Box>
          <Box className="summary-row"><span>Taxa de Entrega</span><strong>{hasProductFreeDelivery ? 'Gratis' : manualDeliveryFee > 0 ? money(manualDeliveryFee) : fee === 0 ? 'Gratis' : money(fee)}</strong></Box>
          {!mesaId && payment !== 'pickup' && deliveryMode(lanchonete) === 'APP' && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {deliveryQuoteLoading
                ? 'Calculando frete pela distância…'
                : deliveryQuoteError
                  ? 'Não foi possível calcular a distância. Tente marcar novamente sua localização.'
                  : deliveryQuote?.distance_km != null
                    ? `Distância aproximada em linha reta: ${Number(deliveryQuote.distance_km).toFixed(2)} km (base R$ ${Number(deliveryQuote.base_fee).toFixed(2)} + R$ ${Number(deliveryQuote.surcharge).toFixed(2)} por distância).`
                    : deliveryQuote?.reason === 'store_location_missing'
                      ? 'A localização da lanchonete ainda não foi cadastrada. Será cobrada apenas a taxa base.'
                      : deliveryQuote && !deliveryQuote.can_calculate_distance
                        ? 'O cálculo por distância está desativado; será cobrada apenas a taxa base.'
                      : deliveryLocation ? '' : 'Marque sua localização para calcular o acréscimo por distância.'}
            </Typography>
          )}
          {discount > 0 && <Box className="summary-row"><span>Desconto</span><strong>- {money(discount)}</strong></Box>}
          <Divider sx={{ my: 1 }} />
          <Box className="summary-row total"><span>Total</span><strong>{money(total)}</strong></Box>
          <Button fullWidth variant="contained" size="large" disabled={loading} onClick={checkout} sx={{ mt: 2 }}>
            {loading ? <CircularProgress size={22} color="inherit" /> : mesaId ? 'Lancar na Mesa' : 'Finalizar pedido'}
          </Button>
        </Paper>
      </Box>
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Forma de Pagamento</DialogTitle>
        <DialogContent>
          <Box className="payment-modal-grid">
            {[
              ['pix', 'Pix'],
              ['card', 'Cartao'],
              ['money', 'Dinheiro'],
              ['pickup', 'Retirada no Local']
            ].filter(([value]) => mesaId !== 'livre' || value !== 'pickup').map(([value, label]) => (
              <Button
                key={value}
                variant={payment === value ? 'contained' : 'outlined'}
                onClick={() => {
                  setPayment(value);
                  if (value !== 'money') {
                    setNeedsChange(false);
                    setChangeAmount('');
                  }
                  setPaymentDialogOpen(false);
                }}
              >
                {label}
              </Button>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
      <PixDialog pix={pix} onClose={() => { const id = pix?.pedidoId; clearCart(); setPix(null); if (id) go('orderDetail', { pedidoId: id }); }} />
      <Snackbar open={!!message} autoHideDuration={3000} onClose={() => setMessage('')} message={message} />
    </>
  );
}

function PixDialog({ pix, onClose }) {
  if (!pix) return null;
  const qrImage = pix.qr
    ? /^(data:image\/|https?:\/\/)/i.test(pix.qr)
      ? pix.qr
      : `data:image/png;base64,${pix.qr}`
    : '';
  const copy = async () => {
    if (pix.code) await navigator.clipboard.writeText(pix.code);
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Pagamento via PIX</DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        {qrImage ? <img className="pix-img" src={qrImage} alt="QR Code PIX" /> : <Box className="pix-placeholder">PIX</Box>}
        <Typography color="text.secondary" sx={{ my: 2 }}>Escaneie o QR Code ou copie o codigo para pagar no banco.</Typography>
        {pix.code && (
          <Paper className="pix-code">
            <Typography noWrap>{pix.code}</Typography>
            <IconButton onClick={copy}><ContentCopy /></IconButton>
          </Paper>
        )}
        {pix.link && <Button fullWidth href={pix.link} target="_blank" variant="outlined" sx={{ mt: 2 }}>Abrir pagamento</Button>}
      </DialogContent>
      <DialogActions><Button fullWidth variant="contained" onClick={onClose}>Ver meus pedidos</Button></DialogActions>
    </Dialog>
  );
}

function OrdersView({ go }) {
  const { guestMode } = useCustomerAuth();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('todos');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (!guestMode) {
        const response = await api.get('/pedidos');
        setOrders(Array.isArray(response.data) ? response.data : []);
        return;
      }

      let ids = [];
      try {
        ids = JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}:guestOrders`) || '[]');
      } catch (_) {
        ids = [];
      }
      const loaded = await Promise.allSettled(ids.map(async (id) => {
        const token = localStorage.getItem(`${STORAGE_PREFIX}:guestOrder:${id}`);
        if (!token) throw new Error('Token ausente');
        const response = await api.get(`/pedidos/guest/${id}`, {
          headers: { 'X-Guest-Order-Token': token }
        });
        return response.data;
      }));
      setOrders(loaded
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } finally {
      setLoading(false);
    }
  }, [guestMode]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);

  const filtered = orders.filter((order) => {
    if (filter === 'todos') return true;
    const date = new Date(order.created_at);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (filter === 'hoje') return date.toDateString() === today.toDateString();
    return date.toDateString() === yesterday.toDateString();
  });

  return (
    <>
      <TopBar title="Meus Pedidos" onBack={() => go('home')} action={<IconButton onClick={load}><ListAlt /></IconButton>} />
      <Box className="screen-body">
        <Box className="chip-scroll">
          {['todos', 'hoje', 'ontem'].map((item) => <Chip key={item} label={item} color={filter === item ? 'primary' : 'default'} onClick={() => setFilter(item)} />)}
        </Box>
        {loading ? <Box className="center"><CircularProgress /></Box> : filtered.length === 0 ? (
          <Box className="empty-state"><ListAlt sx={{ fontSize: 60 }} /><Typography variant="h5" fontWeight={900}>Voce ainda nao tem pedidos</Typography></Box>
        ) : (
          <Box className="order-list">
            {filtered.map((order) => (
              <Paper key={order.id} className="order-card" onClick={() => go('orderDetail', { pedidoId: order.id })}>
                <Box className="order-head" style={{ backgroundColor: statusColor(order.status) }}>
                  <Box>
                    <Typography fontWeight={900}>#{order.id}</Typography>
                    <Typography variant="body2">{new Date(order.created_at).toLocaleDateString()}</Typography>
                  </Box>
                  <Chip label={statusText(order.status)} sx={{ bgcolor: '#fff', fontWeight: 800 }} />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography fontWeight={800}>{order.lanchonete_name || 'Lanchonete'}</Typography>
                  <Typography color="text.secondary">{order.itens?.length || order.items_count || 1} item(ns)</Typography>
                  <Typography color="primary" fontWeight={900}>{money(order.total_amount ?? order.total)}</Typography>
                  {orderNeedsPayment(order) && orderHasPaymentLink(order) && (
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      startIcon={orderIsCardPayment(order) ? <CreditCard /> : <OpenInNew />}
                      sx={{ mt: 1.5 }}
                      onClick={(event) => {
                        event.stopPropagation();
                        window.open(order.payment_link, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      {orderIsCardPayment(order) ? 'Continuar pagamento no cartao' : 'Abrir pagamento'}
                    </Button>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
}

function OrderDetailView({ params, go }) {
  const { guestMode, leaveGuestMode } = useCustomerAuth();
  const { replaceCart, isFromSameLanchonete } = useCart();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [pix, setPix] = useState(null);
  const [customerFeatures, setCustomerFeatures] = useState({});
  const [review, setReview] = useState(null);
  const [rating, setRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [sendingReview, setSendingReview] = useState(false);
  const [reviewMessage, setReviewMessage] = useState('');
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);

  useEffect(() => {
    async function load() {
      const guestToken = localStorage.getItem(`${STORAGE_PREFIX}:guestOrder:${params.pedidoId}`);
      const orderRes = guestToken
        ? await api.get(`/pedidos/guest/${params.pedidoId}`, { headers: { 'X-Guest-Order-Token': guestToken } })
        : await api.get(`/pedidos/${params.pedidoId}`);
      const itemsRes = guestToken
        ? { data: orderRes.data?.items || [] }
        : await api.get(`/pedidos/${params.pedidoId}/items`).catch(() => ({ data: [] }));
      setOrder(orderRes.data);
      setItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);
    }
    load();
  }, [params.pedidoId]);

  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        const guestToken = localStorage.getItem(`${STORAGE_PREFIX}:guestOrder:${params.pedidoId}`);
        const response = guestToken
          ? await api.get(`/pedidos/guest/${params.pedidoId}`, { headers: { 'X-Guest-Order-Token': guestToken } })
          : await api.get(`/pedidos/${params.pedidoId}`);
        setOrder(response.data);
        if (!orderNeedsPayment(response.data)) setPix(null);
      } catch (_) {
        // Mantém a tela utilizável durante oscilações de conexão.
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [params.pedidoId]);

  const isDelivered = ['delivered', 'entregue', 'completed', 'concluido']
    .includes(String(order?.status || '').toLowerCase());

  useEffect(() => {
    if (!order?.lanchonete_id || !isDelivered) {
      return;
    }

    let active = true;
    Promise.all([
      api.get(`/customer-features/public/${order.lanchonete_id}`).catch(() => ({ data: {} })),
      api.get(`/reviews/order/${params.pedidoId}`).catch(() => ({ data: null }))
    ]).then(([featuresResponse, reviewResponse]) => {
      if (!active) return;
      setCustomerFeatures(featuresResponse.data || {});
      setReview(reviewResponse.data || null);
      if (reviewResponse.data) {
        setRating(Number(reviewResponse.data.rating) || 0);
        setReviewComment(reviewResponse.data.comment || '');
      }
    });

    return () => {
      active = false;
    };
  }, [isDelivered, order?.lanchonete_id, params.pedidoId]);

  useEffect(() => {
    if (!order?.lanchonete_id || isDelivered) return;
    api.get(`/customer-features/public/${order.lanchonete_id}`)
      .then(({ data }) => setCustomerFeatures(data || {}))
      .catch(() => setCustomerFeatures({}));
  }, [isDelivered, order?.lanchonete_id]);

  const repeatOrder = () => {
    const sourceItems = items.length ? items : (order.itens || []);
    if (!sourceItems.length) {
      setReviewMessage('Os itens deste pedido nao estao mais disponiveis.');
      return;
    }
    if (!isFromSameLanchonete(order.lanchonete_id) &&
        !window.confirm('Seu carrinho tem produtos de outra loja. Deseja trocar os itens?')) {
      return;
    }

    const nextItems = sourceItems
      .filter((item) => item.produto_id)
      .map((item, index) => {
        const customizations = (Array.isArray(item.customizations) ? item.customizations : []).map((group) => ({
          ...group,
          selectedOptions: group.selectedOptions || group.selected_options || []
        }));
        const basePrice = Number(item.price ?? item.unit_price) || 0;
        const extras = customizations.reduce(
          (total, group) => total + (group.selectedOptions || []).reduce((sum, option) => sum + (Number(option.price) || 0), 0),
          0
        );
        const quantity = Number(item.quantity) || 1;
        const observation = item.observation || item.observations || '';
        return {
          key: `${item.produto_id}-${JSON.stringify(customizations)}-${observation}-${index}`,
          produto_id: item.produto_id,
          name: item.produto_name || item.name,
          price: basePrice,
          unit_price: basePrice + extras,
          quantity,
          subtotal: (basePrice + extras) * quantity,
          image: item.produto_image || item.image,
          lanchonete_id: order.lanchonete_id,
          customizations,
          observation
        };
      });

    if (!nextItems.length) {
      setReviewMessage('Os produtos deste pedido nao podem mais ser repetidos.');
      return;
    }
    replaceCart(nextItems);
    go('cart');
  };

  const submitReview = async () => {
    if (!rating) {
      setReviewMessage('Escolha uma nota de 1 a 5 estrelas.');
      return;
    }

    try {
      setSendingReview(true);
      const response = await api.post('/reviews', {
        pedido_id: Number(params.pedidoId),
        rating,
        comment: reviewComment.trim() || null
      });
      setReview(response.data.review);
      setReviewMessage('Obrigado! Sua avaliacao foi publicada.');
    } catch (error) {
      setReviewMessage(error.response?.data?.error || 'Nao foi possivel enviar sua avaliacao.');
    } finally {
      setSendingReview(false);
    }
  };

  const paymentStatus = String(order?.payment_status || '').toLowerCase();
  const paymentProvider = String(order?.payment_provider || 'mercadopago').toLowerCase();
  const orderStatus = String(order?.status || '').toLowerCase();
  const hasRefundablePayment = paymentProvider === 'mercadopago'
    && ['paid', 'partial_refund', 'partially_refunded'].includes(paymentStatus);
  const isBeforePreparation = ['pending', 'pendente', 'accepted', 'aceito'].includes(orderStatus);
  const refundInProgress = ['processing', 'refund_pending'].includes(String(order?.refund_status || '').toLowerCase());
  const canRequestRefund = !guestMode && hasRefundablePayment && isBeforePreparation && !refundInProgress;

  const requestRefund = async () => {
    if (!canRequestRefund || refunding) return;
    if (!refundReason.trim()) {
      setReviewMessage('Informe o motivo do cancelamento.');
      return;
    }

    try {
      setRefunding(true);
      const response = await api.post(`/pedidos/${order.id}/refund`, {
        reason: refundReason.trim()
      });
      setOrder((current) => ({ ...current, ...(response.data?.pedido || {}) }));
      setRefundDialogOpen(false);
      setRefundReason('');
      setReviewMessage(response.data?.message || 'Pagamento devolvido e pedido cancelado.');
    } catch (error) {
      if (error.response?.data?.pedido) {
        setOrder((current) => ({ ...current, ...error.response.data.pedido }));
        if (error.response.data.code === 'REFUND_PENDING') setRefundDialogOpen(false);
      }
      setReviewMessage(error.response?.data?.error || 'Nao foi possivel devolver o pagamento. Tente novamente.');
    } finally {
      setRefunding(false);
    }
  };

  if (!order) return <Box className="center"><CircularProgress /></Box>;
  const canShowPix = orderNeedsPayment(order) && orderHasPixPayment(order);
  const canShowCardLink = orderNeedsPayment(order) && orderHasPaymentLink(order) && orderIsCardPayment(order);

  return (
    <>
      <TopBar title={`Pedido #${order.id}`} onBack={() => go('orders')} />
      <Box className="screen-body">
        {guestMode && (
          <Alert
            severity="info"
            action={<Button color="inherit" size="small" onClick={leaveGuestMode}>Criar conta</Button>}
            sx={{ mb: 2 }}
          >
            Seu pedido esta salvo neste aparelho. Crie uma conta para facilitar os proximos pedidos.
          </Alert>
        )}
        <Paper className="checkout-card">
          <Chip label={statusText(order.status)} sx={{ bgcolor: statusColor(order.status), color: '#fff', fontWeight: 900 }} />
          <Typography variant="h5" fontWeight={900} sx={{ mt: 2 }}>{order.lanchonete_name}</Typography>
          <Typography color="text.secondary">{new Date(order.created_at).toLocaleString()}</Typography>
          {canShowCardLink && (
            <Button
              variant="contained"
              color="warning"
              fullWidth
              startIcon={<CreditCard />}
              href={order.payment_link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mt: 2 }}
            >
              Continuar pagamento no cartao
            </Button>
          )}
          {canShowPix && (
            <Button
              variant="contained"
              startIcon={<ContentCopy />}
              onClick={() => setPix({
                pedidoId: order.id,
                code: order.pix_qr_code,
                qr: order.pix_qr_code_base64,
                link: order.payment_link
              })}
              sx={{ mt: 2 }}
            >
              Ver QR Code PIX
            </Button>
          )}
          {orderNeedsPayment(order) && orderHasPaymentLink(order) && !orderIsCardPayment(order) && !orderHasPixPayment(order) && (
            <Button
              variant="outlined"
              fullWidth
              startIcon={<OpenInNew />}
              href={order.payment_link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mt: 2 }}
            >
              Abrir link de pagamento
            </Button>
          )}
          {customerFeatures.tracking_enabled === true && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, mt: 3 }}>
              {[
                ['pending', 'Recebido'],
                ['accepted', 'Aceito'],
                ['preparing', 'Preparando'],
                ['delivering', 'Saiu'],
                ['delivered', 'Entregue']
              ].map(([step, label], index) => {
                const aliases = {
                  pendente: 'pending', payment_pending: 'pending', confirmed: 'accepted', aceito: 'accepted',
                  preparando: 'preparing', pronto: 'preparing', ready: 'preparing',
                  saiu_entrega: 'delivering', entregue: 'delivered', completed: 'delivered', concluido: 'delivered'
                };
                const status = aliases[String(order.status || '').toLowerCase()] || String(order.status || '').toLowerCase();
                const statusOrder = ['pending', 'accepted', 'preparing', 'delivering', 'delivered'];
                const completed = statusOrder.indexOf(status) >= index;
                return (
                  <Box key={step} sx={{ textAlign: 'center', minWidth: 0 }}>
                    <Box sx={{ height: 5, borderRadius: 4, bgcolor: completed ? 'success.main' : 'grey.300', mb: 0.8 }} />
                    <Typography variant="caption" fontWeight={completed ? 900 : 500} color={completed ? 'success.main' : 'text.secondary'}>
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
          {order.motoboy_name && (
            <Alert severity="success" icon={<LocalShipping />} sx={{ mt: 2, borderRadius: 2 }}>
              <Typography variant="caption" display="block">Entregador da sua corrida</Typography>
              <Typography fontWeight={900}>{order.motoboy_name}</Typography>
              {order.motoboy_phone && <Typography variant="body2">{order.motoboy_phone}</Typography>}
            </Alert>
          )}
        </Paper>
        <Paper className="checkout-card">
          <Typography fontWeight={900}>Itens</Typography>
          {(items.length ? items : order.itens || []).map((item, index) => (
            <Box key={item.id || index} className="detail-item">
              <Box>
                <Typography fontWeight={800}>{item.produto_name || item.name}</Typography>
                <Typography color="text.secondary">Qtd: {item.quantity}</Typography>
                {(item.observation || item.observations) && (
                  <Typography variant="body2" color="text.secondary">Obs: {item.observation || item.observations}</Typography>
                )}
                {(Array.isArray(item.customizations) ? item.customizations : []).map((group, groupIndex) => (
                  <Typography key={group.group_id || group.groupId || groupIndex} variant="body2" color="text.secondary">
                    {group.group_name || group.groupName}: {(group.selected_options || group.selectedOptions || []).map((option) => option.name).join(', ')}
                  </Typography>
                ))}
              </Box>
              <Typography fontWeight={900}>{money(item.subtotal || (Number(item.unit_price || item.price) * Number(item.quantity)))}</Typography>
            </Box>
          ))}
        </Paper>
        <Paper className="checkout-card">
          <Box className="summary-row"><span>Entrega</span><strong>{money(order.delivery_fee)}</strong></Box>
          <Box className="summary-row total"><span>Total</span><strong>{money(order.total_amount ?? order.total)}</strong></Box>
          <Typography color="text.secondary" sx={{ mt: 2 }}>{order.delivery_address}</Typography>
          {order.house_number && <Typography color="text.secondary">Numero: {order.house_number}</Typography>}
          {order.reference && <Typography color="text.secondary">Referencia: {order.reference}</Typography>}
        </Paper>
        {['processing', 'refund_pending'].includes(String(order.refund_status || '').toLowerCase()) && (
          <Alert severity="info" sx={{ mb: 2 }}>Estorno em processamento pelo provedor de pagamento.</Alert>
        )}
        {String(order.refund_status || '').toLowerCase() === 'refund_failed' && (
          <Alert severity="warning" sx={{ mb: 2 }}>O estorno nao foi concluido. Fale com o suporte para receber ajuda.</Alert>
        )}
        {Number(order.refunded_amount) > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>Valor devolvido: {money(order.refunded_amount)}</Alert>
        )}
        {!guestMode && !['cancelled', 'cancelado'].includes(orderStatus) && (
          <Paper className="checkout-card">
            <Typography variant="h6" fontWeight={900}>Cancelamento e devolucao</Typography>
            {canRequestRefund ? (
              <>
                <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                  Cancele antes do preparo e receba de volta o valor pago pelo Mercado Pago.
                </Typography>
                <Button
                  fullWidth
                  color="error"
                  variant="outlined"
                  onClick={() => setRefundDialogOpen(true)}
                >
                  Cancelar e receber devolucao
                </Button>
              </>
            ) : (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                {!hasRefundablePayment
                  ? 'Este pedido nao possui um pagamento online aprovado pelo Mercado Pago para devolver.'
                  : 'O cancelamento automatico fica disponivel somente antes de o pedido entrar em preparo.'}
              </Alert>
            )}
          </Paper>
        )}
        {isDelivered && customerFeatures.reorder_enabled === true && (
          <Button fullWidth variant="outlined" onClick={repeatOrder} sx={{ mb: 2 }}>
            Repetir este pedido
          </Button>
        )}
        {!guestMode && isDelivered && customerFeatures.reviews_enabled === true && (
          <Paper className="checkout-card">
            <Typography variant="h6" fontWeight={900}>
              {review ? 'Sua avaliacao verificada' : 'Como foi seu pedido?'}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Avalie sua experiencia com {order.lanchonete_name || 'a loja'}.
            </Typography>
            <Rating
              name={`review-${order.id}`}
              value={rating}
              onChange={(_, value) => setRating(value || 0)}
              size="large"
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Conte como foi (opcional)"
              value={reviewComment}
              onChange={(event) => setReviewComment(event.target.value)}
              inputProps={{ maxLength: 500 }}
              sx={{ mt: 2 }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={submitReview}
              disabled={sendingReview || !rating}
              sx={{ mt: 2 }}
            >
              {sendingReview ? 'Enviando...' : review ? 'Atualizar avaliacao' : 'Enviar avaliacao'}
            </Button>
          </Paper>
        )}
      </Box>
      <PixDialog pix={pix} onClose={() => setPix(null)} />
      <Dialog
        open={refundDialogOpen}
        onClose={() => !refunding && setRefundDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cancelar pedido e devolver pagamento?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            O pedido sera cancelado e o valor ainda nao devolvido sera enviado pelo Mercado Pago ao mesmo meio de pagamento.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            required
            multiline
            minRows={3}
            label="Motivo do cancelamento"
            value={refundReason}
            onChange={(event) => setRefundReason(event.target.value)}
            inputProps={{ maxLength: 300 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)} disabled={refunding}>Voltar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={requestRefund}
            disabled={refunding || !refundReason.trim()}
          >
            {refunding ? 'Devolvendo...' : 'Confirmar cancelamento'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!reviewMessage}
        autoHideDuration={3500}
        onClose={() => setReviewMessage('')}
        message={reviewMessage}
      />
    </>
  );
}

function ProfileView({ go }) {
  const { user, logout, updateProfile, changePassword, deleteAccount } = useCustomerAuth();
  const [form, setForm] = useState(user || {});
  const [serviceCity, setServiceCity] = useState(user?.service_city || '');
  const [savingCity, setSavingCity] = useState(false);
  const [cityInputFocused, setCityInputFocused] = useState(false);
  const [citySelected, setCitySelected] = useState(Boolean(user?.service_city));
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(false);
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [motoboyPayout, setMotoboyPayout] = useState(null);
  const [pixDialogOpen, setPixDialogOpen] = useState(false);
  const [pixKey, setPixKey] = useState('');
  const [pixType, setPixType] = useState('CPF');
  const [pixSaving, setPixSaving] = useState(false);
  const cityQuery = normalizeCitySearch(serviceCity);
  const citySuggestions = cityInputFocused && !citySelected && cityQuery.length >= 2
    ? municipalities.filter((city) => normalizeCitySearch(city.name).includes(cityQuery)).slice(0, 8)
    : [];

  useEffect(() => {
    if (user?.user_type !== 'motoboy' || user?.lanchonete_id || String(user?.motoboy_scope || 'APP').toUpperCase() === 'LOJA') return;
    api.get('/motoboys/payouts/me').then((response) => setMotoboyPayout(response.data)).catch((error) => {
      setMotoboyPayout({ load_error: error.response?.data?.error || 'Não foi possível carregar seus dados Pix.' });
    });
  }, [user?.id, user?.user_type, user?.lanchonete_id, user?.motoboy_scope]);

  const saveMotoboyPix = async () => {
    try {
      setPixSaving(true);
      await api.put('/motoboys/payouts/pix-key', { key: pixKey, type: pixType });
      const response = await api.get('/motoboys/payouts/me');
      setMotoboyPayout(response.data);
      setPixDialogOpen(false);
      setPixKey('');
      setMessage('Chave Pix salva com proteção.');
    } catch (error) {
      setMessage(error.response?.data?.error || 'Não foi possível salvar a chave Pix.');
    } finally {
      setPixSaving(false);
    }
  };

  const save = async () => {
    const result = await updateProfile(form);
    setMessage(result.success ? 'Perfil atualizado' : result.error);
    if (result.success) setEditing(false);
  };

  const saveServiceCity = async () => {
    const city = serviceCity.trim();
    if (!city) {
      setMessage('Informe a cidade onde deseja receber corridas.');
      return;
    }
    setSavingCity(true);
    const result = await updateProfile({ service_city: city });
    setMessage(result.success ? 'Cidade de atendimento atualizada.' : result.error);
    if (result.success) {
      setServiceCity(city);
      setCitySelected(true);
    }
    setSavingCity(false);
  };

  const savePassword = async () => {
    if (!passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword) {
      setMessage('Preencha todos os campos de senha');
      return;
    }
    if (passwords.newPassword.length < 6) {
      setMessage('A nova senha precisa ter pelo menos 6 caracteres');
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      setMessage('As senhas nao conferem');
      return;
    }
    const result = await changePassword(passwords.currentPassword, passwords.newPassword);
    setMessage(result.success ? 'Senha alterada com sucesso' : result.error);
    if (result.success) setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  const confirmDelete = async () => {
    if (!deletePassword) {
      setMessage('Informe sua senha para excluir a conta');
      return;
    }
    const result = await deleteAccount(deletePassword);
    if (!result.success) setMessage(result.error);
  };

  return (
    <>
      <Box className="profile-hero">
        <IconButton className="profile-back" onClick={() => go('home')}><ArrowBack /></IconButton>
        <Typography variant="h4" fontWeight={900}>Meu Perfil</Typography>
      </Box>
      <Box className="screen-body profile-screen">
        <Paper className="profile-main-card">
          <Box className="profile-photo-wrap">
            <Avatar className="profile-photo" src={assetUrl(user?.image)}><Person /></Avatar>
            <Box className="profile-camera">📷</Box>
          </Box>
          <Typography variant="h5" fontWeight={900}>{user?.name || 'Meu perfil'}</Typography>
          <Typography color="text.secondary">{user?.email}</Typography>
          <Button fullWidth variant="contained" size="large" onClick={() => setEditing(!editing)} className="profile-edit-button">
            Editar meus dados
          </Button>
        </Paper>

        {user?.user_type === 'motoboy' && !user?.lanchonete_id && String(user?.motoboy_scope || 'APP').toUpperCase() !== 'LOJA' && (
          <Paper className="profile-section-card">
            <Typography variant="h5" fontWeight={900}>Cidade onde quero trabalhar</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
              As corridas das lanchonetes desta cidade aparecerão para você.
            </Typography>
            <TextField
              fullWidth
              label="Cidade de atendimento"
              value={serviceCity}
              onFocus={() => { setCityInputFocused(true); setCitySelected(false); }}
              onBlur={() => setTimeout(() => setCityInputFocused(false), 150)}
              onChange={(event) => { setCitySelected(false); setServiceCity(event.target.value); }}
              inputProps={{ maxLength: 120 }}
              placeholder="Digite o nome da cidade"
            />
            {citySuggestions.length > 0 && (
              <Paper variant="outlined" sx={{ mt: 0.5, maxHeight: 240, overflowY: 'auto', borderRadius: 2 }}>
                {citySuggestions.map((city) => (
                  <Button
                    key={`${city.name}-${city.uf}`}
                    fullWidth
                    color="inherit"
                    startIcon={<LocationOn />}
                    sx={{ justifyContent: 'flex-start', borderRadius: 0, minHeight: 44, textTransform: 'none' }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      setServiceCity(`${city.name} - ${city.uf}`);
                      setCitySelected(true);
                      setCityInputFocused(false);
                    }}
                  >
                    {city.name} - {city.uf}
                  </Button>
                ))}
              </Paper>
            )}
            <Button
              fullWidth
              sx={{ mt: 1.5 }}
              variant="contained"
              startIcon={savingCity ? <CircularProgress size={18} color="inherit" /> : <LocalShipping />}
              onClick={saveServiceCity}
              disabled={savingCity || !serviceCity.trim()}
            >
              {savingCity ? 'Salvando...' : 'Salvar cidade de atendimento'}
            </Button>
          </Paper>
        )}

        {user?.user_type === 'motoboy' && !user?.lanchonete_id && String(user?.motoboy_scope || 'APP').toUpperCase() !== 'LOJA' && (
          <Paper className="profile-section-card">
            <Typography variant="h5" fontWeight={900}>Chave Pix para receber</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
              {motoboyPayout?.has_pix_key ? `Chave cadastrada: ${motoboyPayout.pix_key_masked}` : 'Cadastre sua chave para solicitar saques.'}
            </Typography>
            {motoboyPayout?.load_error && <Alert severity="warning" sx={{ mb: 1 }}>{motoboyPayout.load_error}</Alert>}
            <Button variant="outlined" onClick={() => setPixDialogOpen(true)} disabled={motoboyPayout?.pix_key_setup_ready === false}>
              {motoboyPayout?.has_pix_key ? 'Alterar chave Pix' : 'Cadastrar chave Pix'}
            </Button>
          </Paper>
        )}

        <Alert severity="info" className="profile-info-alert">
          Seus dados aparecem logo abaixo. Toque em "Editar meus dados" para alterar nome, telefone ou endereco.
        </Alert>

        <Paper className="profile-section-card">
          <Typography variant="h5" fontWeight={900}>Dados Pessoais</Typography>
          <Box className="profile-section-line" />
          {[
            ['Nome', form.name || '', '👤', 'name'],
            ['Telefone', form.phone || '', '📞', 'phone'],
            ['Endereco', form.address || '', '📍', 'address'],
            ['Numero', form.house_number || '', '🏠', 'house_number'],
            ['Email', form.email || user?.email || '', '📧', 'email'],
            ['Referencia', form.reference || '', '📍', 'reference']
          ].map(([label, value, icon, key]) => (
            <Box key={key} className="profile-data-row">
              <Box className="profile-row-icon">{icon}</Box>
              <Box sx={{ flex: 1 }}>
                <Typography color="text.secondary" fontWeight={800}>{label}</Typography>
                {editing && key !== 'email' ? (
                  <TextField
                    fullWidth
                    variant="standard"
                    value={form[key] || ''}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  />
                ) : (
                  <Typography fontWeight={900}>{value || 'Nao informado'}</Typography>
                )}
              </Box>
            </Box>
          ))}
          {editing && <Button variant="contained" size="large" onClick={save}>Salvar dados</Button>}
        </Paper>

        <Paper className="profile-section-card">
          <Typography variant="h5" fontWeight={900}>Seguranca</Typography>
          <Box className="profile-section-line" />
          <TextField label="Senha Atual" type="password" value={passwords.currentPassword} onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })} />
          <TextField label="Nova Senha" type="password" value={passwords.newPassword} onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })} />
          <TextField label="Confirmar Nova Senha" type="password" value={passwords.confirmPassword} onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })} />
          <Button variant="contained" size="large" onClick={savePassword}>Alterar Senha</Button>
        </Paper>

        <Button className="whatsapp-support" variant="contained" href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '5500000000000'}`} target="_blank">
          Suporte pelo WhatsApp
        </Button>

        <Paper className="danger-zone">
          <Typography variant="h6" fontWeight={900}>Zona de perigo</Typography>
          <Typography>A exclusao remove sua conta e dados de acesso. Esta acao nao pode ser desfeita.</Typography>
          <Button variant="outlined" color="error" size="large" onClick={() => setDeleteDialogOpen(true)}>Excluir minha conta</Button>
        </Paper>

        <Paper className="logout-card">
          <Button variant="outlined" color="error" size="large" onClick={logout} startIcon={<Logout />}>Sair do Aplicativo</Button>
        </Paper>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Excluir minha conta</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>Digite sua senha para confirmar.</Typography>
          <TextField fullWidth type="password" label="Senha" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>Excluir</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={pixDialogOpen} onClose={() => !pixSaving && setPixDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Chave Pix para receber</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>A chave será protegida e ficará disponível para o Admin Master quando você pedir o saque.</Typography>
          <TextField select fullWidth label="Tipo de chave" value={pixType} onChange={(event) => setPixType(event.target.value)} sx={{ mb: 2 }}>
            {['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP'].map((type) => <MenuItem key={type} value={type}>{type === 'EVP' ? 'Aleatória' : type}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Chave Pix" value={pixKey} onChange={(event) => setPixKey(event.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPixDialogOpen(false)} disabled={pixSaving}>Cancelar</Button>
          <Button variant="contained" onClick={saveMotoboyPix} disabled={pixSaving || !pixKey.trim()}>{pixSaving ? 'Salvando...' : 'Salvar chave Pix'}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!message} autoHideDuration={2500} onClose={() => setMessage('')} message={message} />
    </>
  );
}

function NotificationsView({ go }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get('/notifications').then((response) => setItems(Array.isArray(response.data) ? response.data : [])).catch(() => setItems([]));
  }, []);
  return (
    <>
      <TopBar title="Notificacoes" onBack={() => go('home')} />
      <Box className="screen-body">
        {items.length === 0 ? <Box className="empty-state"><Notifications sx={{ fontSize: 60 }} /><Typography variant="h5" fontWeight={900}>Nenhuma notificacao</Typography></Box> : (
          items.map((item) => (
            <Paper key={item.id} className="checkout-card">
              <Typography fontWeight={900}>{item.title}</Typography>
              <Typography color="text.secondary">{item.message}</Typography>
            </Paper>
          ))
        )}
      </Box>
    </>
  );
}

function ChatView({ go }) {
  const [messages, setMessages] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await api.get('/chat/messages');
      setMessages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Nao foi possivel carregar as mensagens.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = window.setInterval(load, 12000);
    return () => window.clearInterval(interval);
  }, [load]);

  const conversations = Object.values(messages.reduce((result, item) => {
    if (!result[item.pedido_id]) result[item.pedido_id] = { ...item, messages: [] };
    result[item.pedido_id].messages.push(item);
    return result;
  }, {}));

  const send = async (conversation) => {
    const text = String(drafts[conversation.pedido_id] || '').trim();
    if (!text || sendingId === conversation.pedido_id) return;

    try {
      setSendingId(conversation.pedido_id);
      await api.post('/chat/messages', { pedidoId: conversation.pedido_id, message: text });
      setDrafts((current) => ({ ...current, [conversation.pedido_id]: '' }));
      await load();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Nao foi possivel enviar a mensagem.');
    } finally {
      setSendingId(null);
    }
  };

  return (
    <>
      <TopBar title="Mensagens" onBack={() => go('home')} action={<Chat />} />
      <Box className="screen-body chat-screen">
        {loading ? <Box className="center"><CircularProgress /></Box> : conversations.length === 0 ? (
          <Box className="empty-state">
            <Chat sx={{ fontSize: 60, color: '#10B981' }} />
            <Typography variant="h5" fontWeight={900}>Nenhuma mensagem ainda</Typography>
            <Typography color="text.secondary">As mensagens sobre seus pedidos aparecerao aqui.</Typography>
          </Box>
        ) : conversations.map((conversation) => (
          <Paper key={conversation.pedido_id} className="chat-card">
            <Box className="chat-store-header">
              <Avatar src={assetUrl(conversation.lanchonete_image)}><Storefront /></Avatar>
              <Box>
                <Typography fontWeight={900}>{conversation.lanchonete_name || 'Lanchonete'}</Typography>
                <Typography variant="body2">Pedido #{conversation.lanchonete_order_number || conversation.pedido_id}</Typography>
              </Box>
            </Box>
            <Box className="chat-thread">
              {conversation.messages.map((item) => {
                const mine = item.sender_type === 'cliente';
                return (
                  <Box key={item.id} className={`chat-message-row ${mine ? 'mine' : ''}`}>
                    <Box className={`chat-bubble ${mine ? 'mine' : ''}`}>
                      <Typography variant="body2">{item.message}</Typography>
                      <Typography variant="caption">
                        {mine ? 'Voce' : conversation.lanchonete_name || 'Lanchonete'} - {new Date(item.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Box className="chat-composer">
              <TextField
                fullWidth
                size="small"
                placeholder="Digite sua mensagem"
                value={drafts[conversation.pedido_id] || ''}
                inputProps={{ maxLength: 1000 }}
                onChange={(event) => setDrafts((current) => ({ ...current, [conversation.pedido_id]: event.target.value }))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    send(conversation);
                  }
                }}
              />
              <IconButton color="primary" disabled={sendingId === conversation.pedido_id} onClick={() => send(conversation)}><Send /></IconButton>
            </Box>
          </Paper>
        ))}
      </Box>
      <Snackbar open={!!message} autoHideDuration={3000} onClose={() => setMessage('')} message={message} />
    </>
  );
}

function MotoboyView({ go }) {
  const { user } = useCustomerAuth();
  const [available, setAvailable] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [tab, setTab] = useState('available');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [pixDialogOpen, setPixDialogOpen] = useState(false);
  const [pixKey, setPixKey] = useState('');
  const [pixType, setPixType] = useState('CPF');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [availableResult, deliveriesResult, callsResult, myCallsResult] = await Promise.allSettled([
        api.get('/pedidos/available/delivery'),
        api.get('/pedidos/my-deliveries'),
        api.get('/delivery-calls/available'),
        api.get('/delivery-calls/mine')
      ]);

      const normalAvailable = availableResult.status === 'fulfilled' && Array.isArray(availableResult.value.data)
        ? availableResult.value.data : [];
      const availableCalls = callsResult.status === 'fulfilled' && Array.isArray(callsResult.value.data)
        ? callsResult.value.data : [];
      const normalDeliveries = deliveriesResult.status === 'fulfilled' && Array.isArray(deliveriesResult.value.data)
        ? deliveriesResult.value.data : [];
      const acceptedCalls = myCallsResult.status === 'fulfilled' && Array.isArray(myCallsResult.value.data)
        ? myCallsResult.value.data : [];

      setAvailable([...normalAvailable, ...availableCalls]);
      setDeliveries([...normalDeliveries, ...acceptedCalls]);

      if (user && !user.lanchonete_id && String(user.motoboy_scope || 'APP').toUpperCase() !== 'LOJA') {
        try {
          const payout = await api.get('/motoboys/payouts/me');
          setPayoutSummary(payout.data);
        } catch (error) {
          setPayoutSummary({ load_error: error?.response?.data?.error || 'Não foi possível carregar o saldo.' });
        }
      } else {
        setPayoutSummary(null);
      }

      const failedResult = [availableResult, deliveriesResult, callsResult, myCallsResult]
        .find((result) => result.status === 'rejected');
      if (failedResult) {
        const error = failedResult.reason;
        setMessage(error?.response?.data?.error || 'Uma parte das entregas nao pôde ser atualizada. Tente novamente.');
        console.error('Erro ao atualizar entregas do motoboy no PWA:', error);
      }
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Nao foi possivel atualizar as entregas.');
      console.error('Erro ao atualizar entregas do motoboy no PWA:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);

  const accepted = deliveries.filter((order) => ['saiu_entrega', 'delivering', 'preparando', 'preparing'].includes(String(order.status || '').toLowerCase()));
  const delivered = deliveries.filter((order) => ['entregue', 'delivered'].includes(String(order.status || '').toLowerCase()));
  const current = tab === 'available' ? available : tab === 'accepted' ? accepted : delivered;

  const acceptOrder = async (order) => {
    try {
      const path = order?.entity_type === 'delivery_call'
        ? `/delivery-calls/${order.id}/accept`
        : `/pedidos/${order.id}/accept`;
      await api.post(path);
      setMessage('Corrida aceita');
      setSelectedOrder(null);
      setTab('accepted');
      await load();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Nao foi possivel aceitar');
    }
  };

  const completeOrder = async (order) => {
    try {
      const path = order?.entity_type === 'delivery_call'
        ? `/delivery-calls/${order.id}/complete`
        : `/pedidos/${order.id}/complete`;
      await api.put(path);
      setMessage('Entrega concluida');
      setSelectedOrder(null);
      setTab('delivered');
      await load();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Nao foi possivel finalizar');
    }
  };

  const savePixKey = async () => {
    try {
      setPayoutBusy(true);
      await api.put('/motoboys/payouts/pix-key', { key: pixKey, type: pixType });
      const payout = await api.get('/motoboys/payouts/me');
      setPayoutSummary(payout.data);
      setPixDialogOpen(false);
      setPixKey('');
      setMessage('Chave Pix salva com proteção.');
    } catch (error) {
      setMessage(error.response?.data?.error || 'Não foi possível salvar a chave Pix.');
    } finally {
      setPayoutBusy(false);
    }
  };

  const requestPayout = async () => {
    if (payoutSummary?.load_error) {
      setMessage(payoutSummary.load_error);
      return;
    }
    if (!payoutSummary?.pix_key_setup_ready) {
      setMessage('Cadastro de Pix indisponível. Avise o Admin Master.');
      return;
    }
    if (!payoutSummary?.has_pix_key) {
      setPixDialogOpen(true);
      return;
    }
    if (payoutSummary?.has_open_request) {
      setMessage('Você já tem um saque aguardando pagamento.');
      return;
    }
    if (!Number(payoutSummary?.eligible_delivery_count || 0)) {
      setMessage('Ainda não há entregas concluídas disponíveis para saque.');
      return;
    }
    if (!window.confirm(`Solicitar ${payoutSummary.eligible_delivery_count} entrega(s), total de ${money(payoutSummary.eligible_amount)}? O Admin Master receberá o pedido para pagar manualmente pelo Pix.`)) return;
    try {
      setPayoutBusy(true);
      await api.post('/motoboys/payouts/request');
      const payout = await api.get('/motoboys/payouts/me');
      setPayoutSummary(payout.data);
      setMessage('Solicitação enviada ao Admin Master. A previsão informada é às 00:00.');
    } catch (error) {
      setMessage(error.response?.data?.error || 'Não foi possível solicitar o saque.');
    } finally {
      setPayoutBusy(false);
    }
  };

  const openDeliveryRoute = (order) => {
    const hasLatitude = order?.delivery_latitude !== null && order?.delivery_latitude !== undefined && order?.delivery_latitude !== '';
    const hasLongitude = order?.delivery_longitude !== null && order?.delivery_longitude !== undefined && order?.delivery_longitude !== '';
    const latitude = Number(order?.delivery_latitude);
    const longitude = Number(order?.delivery_longitude);
    const hasCoordinates = hasLatitude && hasLongitude && Number.isFinite(latitude) && latitude >= -90 && latitude <= 90 &&
      Number.isFinite(longitude) && longitude >= -180 && longitude <= 180;
    const destination = hasCoordinates
      ? `${latitude},${longitude}`
      : [order?.delivery_address, order?.house_number, order?.reference].filter(Boolean).join(', ');

    if (!destination) {
      setMessage('Este pedido nao possui localizacao nem endereco para abrir a rota.');
      return;
    }

    const routeUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=driving`;
    window.open(routeUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Box className="motoboy-header">
        <Box className="header-row">
          <Box>
            <Typography variant="h5" fontWeight={900}>Ola, {user?.name?.split(' ')[0] || 'Motoboy'}</Typography>
            <Typography fontWeight={800}>Motoboy App</Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <WebPushButton user={user} />
            <Avatar className="motoboy-avatar"><Person /></Avatar>
          </Box>
        </Box>
        <Box className="motoboy-stats">
          <Box><strong>{available.length}</strong><span>Disponiveis</span></Box>
          <Box><strong>{accepted.length}</strong><span>Aceitos</span></Box>
          <Box><strong>{delivered.length}</strong><span>Entregues</span></Box>
        </Box>
        <Box className="motoboy-tabs">
          {[
            ['available', 'Para Aceitar'],
            ['accepted', 'Aceitos'],
            ['delivered', 'Entregues']
          ].map(([value, label]) => <Button key={value} className={tab === value ? 'active' : ''} onClick={() => setTab(value)}>{label}</Button>)}
        </Box>
      </Box>
      <Box className="screen-body motoboy-screen-body">
        {user && !user.lanchonete_id && String(user.motoboy_scope || 'APP').toUpperCase() !== 'LOJA' && (
          <Paper className="checkout-card" sx={{ p: 2, mb: 2, border: '1px solid #A7F3D0', bgcolor: '#ECFDF5' }}>
            <Typography fontWeight={900}>Entregas a receber</Typography>
            {payoutSummary?.load_error ? (
              <Alert severity="warning" sx={{ mt: 1 }}>{payoutSummary.load_error} <Button size="small" onClick={load}>Tentar novamente</Button></Alert>
            ) : <>
              <Typography variant="h4" color="success.main" fontWeight={900}>{money(payoutSummary?.outstanding_amount || 0)}</Typography>
              <Typography color="text.secondary">{Number(payoutSummary?.outstanding_delivery_count || 0)} entrega(s) pendentes</Typography>
              <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                <Button variant="contained" color="success" disabled={payoutBusy || payoutSummary?.has_open_request || !payoutSummary?.pix_key_setup_ready} onClick={requestPayout}>
                  {payoutBusy ? 'Aguarde...' : payoutSummary?.has_open_request ? 'Aguardando pagamento' : 'Sacar'}
                </Button>
                <Button variant="outlined" onClick={() => setPixDialogOpen(true)} disabled={!payoutSummary?.pix_key_setup_ready}>
                  {payoutSummary?.has_pix_key ? `Pix ${payoutSummary.pix_key_masked} · Alterar` : 'Cadastrar chave Pix'}
                </Button>
              </Box>
              {!payoutSummary?.pix_key_setup_ready && <Typography variant="caption" display="block" sx={{ mt: 1 }}>Cadastro de Pix temporariamente indisponível; avise o Admin Master.</Typography>}
              {!payoutSummary?.payouts_enabled && payoutSummary?.pix_key_setup_ready && <Typography variant="caption" display="block" sx={{ mt: 1 }}>O Admin Master fará o pagamento manual pelo Mercado Pago e confirmará aqui.</Typography>}
              {payoutSummary?.history?.[0] && <Typography variant="caption" display="block" sx={{ mt: 1 }}>Última solicitação: {payoutSummary.history[0].status}</Typography>}
            </>}
          </Paper>
        )}
        <Paper className="checkout-card motoboy-income">
          <Box>
            <Typography color="text.secondary" fontWeight={800}>Ganhos acumulados</Typography>
            <Typography variant="h4" color="primary" fontWeight={900}>{money(delivered.length * APP_DELIVERY_FEE)}</Typography>
          </Box>
          <Box className="income-icon"><ShoppingCart /></Box>
        </Paper>
        {loading ? <Box className="center"><CircularProgress /></Box> : current.length === 0 ? (
          <Paper className="motoboy-empty">
            <Search sx={{ fontSize: 74 }} />
            <Typography>Nenhuma corrida disponivel no momento.</Typography>
            <Button variant="contained" onClick={load}>Verificar Novamente</Button>
          </Paper>
        ) : current.map((order) => (
          <Paper key={`${order.entity_type || 'order'}-${order.id}`} className="checkout-card" onClick={() => setSelectedOrder(order)} sx={{ cursor: 'pointer' }}>
            <Box className="store-top">
              <Typography fontWeight={900}>{order.entity_type === 'delivery_call' ? 'Corrida' : 'Pedido'} #{order.id}</Typography>
              <Chip label={statusText(order.status)} />
            </Box>
            <Typography>{order.lanchonete_name || 'Lanchonete'}</Typography>
            <Typography color="text.secondary">{order.delivery_address}</Typography>
            {order.house_number && <Typography color="text.secondary">Numero: {order.house_number}</Typography>}
            {tab !== 'delivered' && <Button variant="outlined" startIcon={<Directions />} onClick={(event) => { event.stopPropagation(); openDeliveryRoute(order); }}>Abrir rota</Button>}
            {tab === 'available' && <Button variant="contained" onClick={(event) => { event.stopPropagation(); acceptOrder(order); }}>Aceitar</Button>}
            {tab === 'accepted' && <Button variant="contained" onClick={(event) => { event.stopPropagation(); completeOrder(order); }}>Finalizar entrega</Button>}
          </Paper>
        ))}
      </Box>
      <Dialog open={!!selectedOrder} onClose={() => setSelectedOrder(null)} fullWidth maxWidth="xs">
        <DialogTitle>Pedido #{selectedOrder?.id}</DialogTitle>
        <DialogContent>
          <Typography fontWeight={900}>Cliente</Typography>
          <Typography color="text.secondary">{selectedOrder?.customer_name || 'Cliente'}</Typography>
          <Typography color="text.secondary">{selectedOrder?.customer_phone || selectedOrder?.customer_phone_number || 'Telefone nao informado'}</Typography>
          <Divider sx={{ my: 2 }} />
          <Typography fontWeight={900}>Entrega</Typography>
          <Typography color="text.secondary">{selectedOrder?.delivery_address}</Typography>
          {selectedOrder?.house_number && <Typography color="text.secondary">Numero: {selectedOrder.house_number}</Typography>}
          {selectedOrder?.reference && <Typography color="text.secondary">Referencia: {selectedOrder.reference}</Typography>}
          <Divider sx={{ my: 2 }} />
          {tab === 'delivered' ? (
            <>
              <Typography fontWeight={900}>Ganho do motoboy</Typography>
              <Typography color="primary" variant="h5" fontWeight={900}>{money(APP_DELIVERY_FEE)}</Typography>
            </>
          ) : (
            <>
              <Typography fontWeight={900}>Pagamento do pedido</Typography>
              <Typography color="text.secondary">{String(selectedOrder?.payment_method || 'Nao informado').toUpperCase()}</Typography>
              <Typography color="primary" fontWeight={900}>{money(selectedOrder?.total_amount ?? selectedOrder?.total)}</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions className="motoboy-dialog-actions">
          {tab === 'available' && <Button variant="contained" onClick={() => acceptOrder(selectedOrder)}>Aceitar pedido</Button>}
          {tab === 'accepted' && <Button variant="contained" onClick={() => completeOrder(selectedOrder)}>Finalizar entrega</Button>}
          <Button variant="contained" startIcon={<Directions />} onClick={() => openDeliveryRoute(selectedOrder)}>Abrir rota</Button>
          <Button onClick={() => setSelectedOrder(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={pixDialogOpen} onClose={() => !payoutBusy && setPixDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Chave Pix para receber</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>O Admin Master verá a chave quando você solicitar o saque. Ela será armazenada protegida.</Typography>
          <TextField select fullWidth label="Tipo de chave" value={pixType} onChange={(event) => setPixType(event.target.value)} sx={{ mb: 2 }}>
            {['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP'].map((type) => <MenuItem key={type} value={type}>{type === 'EVP' ? 'Aleatória' : type}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Chave Pix" value={pixKey} onChange={(event) => setPixKey(event.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPixDialogOpen(false)} disabled={payoutBusy}>Cancelar</Button>
          <Button variant="contained" onClick={savePixKey} disabled={payoutBusy || !pixKey.trim()}>{payoutBusy ? 'Salvando...' : 'Salvar chave Pix'}</Button>
        </DialogActions>
      </Dialog>
      <RoleBottomNav
        active="deliveries"
        primaryLabel="Entregas"
        primaryIcon={<Storefront />}
        onPrimary={() => go('motoboy')}
        onProfile={() => go('profile')}
      />
      <Snackbar open={!!message} autoHideDuration={2500} onClose={() => setMessage('')} message={message} />
    </>
  );
}

function WaiterView({ go }) {
  const [mesas, setMesas] = useState([]);
  const [selectedMesa, setSelectedMesa] = useState(null);
  const [mesaDetails, setMesaDetails] = useState(null);
  const [loadingMesaDetails, setLoadingMesaDetails] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await api.get('/mesas');
      setMesas(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Nao foi possivel atualizar as mesas');
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);

  const openMesa = async () => {
    if (!customerName.trim()) {
      setMessage('Informe o nome do cliente');
      return;
    }
    try {
      await api.post(`/mesas/${selectedMesa.id}/open`, { customer_name: customerName.trim() });
      setSelectedMesa(null);
      setCustomerName('');
      load();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Nao foi possivel abrir a mesa');
    }
  };

  const closeMesa = async (mesa) => {
    if (!window.confirm(`Fechar a Mesa ${mesa.numero} e liberar para outro cliente? Confira o pagamento antes de continuar.`)) return;
    try {
      await api.post(`/mesas/${mesa.id}/close`);
      setSelectedMesa(null);
      setMesaDetails(null);
      load();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Nao foi possivel fechar a mesa');
    }
  };

  const openMesaDialog = async (mesa) => {
    if (mesa.id === 'livre') {
      go('store', { lanchoneteId: mesa.lanchonete_id, mesaId: 'livre' });
      return;
    }
    setSelectedMesa(mesa);
    setMesaDetails(null);
    if (mesa.status === 'disponivel') return;

    setLoadingMesaDetails(true);
    try {
      const response = await api.get(`/mesas/${mesa.id}/details`);
      setMesaDetails(response.data);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Nao foi possivel carregar os pedidos da mesa');
    } finally {
      setLoadingMesaDetails(false);
    }
  };

  const mesasWithLivre = React.useMemo(() => {
    if (mesas.length === 0) return [];
    return [
      { id: 'livre', numero: 'Livre', status: 'disponivel', isVirtual: true, lanchonete_id: mesas[0]?.lanchonete_id },
      ...mesas
    ];
  }, [mesas]);

  return (
    <>
      <Box className="waiter-header">
        <Typography variant="h5" fontWeight={900}>Painel do Garcom</Typography>
        <Typography>Gerenciamento de Atendimento</Typography>
      </Box>
      <Box className="screen-body waiter-grid">
        {mesasWithLivre.map((mesa) => (
          <Paper key={mesa.id} className={`mesa-card ${mesa.id === 'livre' ? 'quick' : (mesa.status === 'disponivel' ? 'free' : 'busy')}`} onClick={() => openMesaDialog(mesa)}>
            <Restaurant />
            <Typography fontWeight={900}>{mesa.id === 'livre' ? 'Mesa Livre' : `Mesa ${mesa.numero}`}</Typography>
            <Typography>{mesa.id === 'livre' ? 'Pedido Rápido' : (mesa.status === 'disponivel' ? 'Livre' : 'Ocupada')}</Typography>
            {mesa.customer_name && <Typography variant="body2">{mesa.customer_name}</Typography>}
            {mesa.status !== 'disponivel' && mesa.id !== 'livre' && <Button color="error" onClick={(e) => { e.stopPropagation(); closeMesa(mesa); }}>Fechar</Button>}
          </Paper>
        ))}
      </Box>
      <Dialog open={!!selectedMesa} onClose={() => setSelectedMesa(null)} fullWidth maxWidth="xs">
        <DialogTitle>{selectedMesa?.status === 'disponivel' ? `Abrir Mesa ${selectedMesa?.numero}` : `Mesa ${selectedMesa?.numero}`}</DialogTitle>
        <DialogContent>
          {selectedMesa?.status === 'disponivel' ? (
            <TextField fullWidth label="Nome do cliente" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
          ) : loadingMesaDetails ? (
            <Box className="center"><CircularProgress /></Box>
          ) : (
            <Box>
              <Typography fontWeight={900}>Mesa ocupada por {mesaDetails?.mesa?.customer_name || selectedMesa?.customer_name || 'cliente'}</Typography>
              <Typography color="primary" fontWeight={900} sx={{ mt: 1 }}>Total: {money(mesaDetails?.total_parcial || 0)}</Typography>
              <Divider sx={{ my: 2 }} />
              {!mesaDetails?.orders?.length ? (
                <Typography color="text.secondary">Nenhum pedido lancado nesta mesa ainda.</Typography>
              ) : (
                mesaDetails.orders.map((order) => (
                  <Paper key={order.id} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                    <Box className="summary-row"><span>Pedido #{order.id}</span><strong>{money(order.total_amount ?? order.total)}</strong></Box>
                    {(order.items || order.itens || []).map((item, index) => (
                      <Typography key={item.id || index} variant="body2" color="text.secondary">
                        {item.quantity}x {item.produto_name || item.product_name || item.name} - {money(item.subtotal || (Number(item.price) * Number(item.quantity)))}
                      </Typography>
                    ))}
                  </Paper>
                ))
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedMesa(null)}>Cancelar</Button>
          {selectedMesa?.status === 'disponivel' && <Button variant="contained" onClick={openMesa}>Abrir Mesa</Button>}
          {selectedMesa?.status !== 'disponivel' && <Button variant="contained" onClick={() => go('store', { lanchoneteId: selectedMesa.lanchonete_id, mesaId: selectedMesa.id })}>Lancar Itens</Button>}
        </DialogActions>
      </Dialog>
      <RoleBottomNav
        active="tables"
        primaryLabel="Mesas"
        primaryIcon={<Restaurant />}
        onPrimary={() => go('waiter')}
        onProfile={() => go('profile')}
      />
      <Snackbar open={!!message} autoHideDuration={2500} onClose={() => setMessage('')} message={message} />
    </>
  );
}

function TopBar({ title, onBack, action }) {
  return (
    <Box className="top-bar">
      <IconButton onClick={onBack}><ArrowBack /></IconButton>
      <Typography variant="h6" fontWeight={900}>{title}</Typography>
      {action || <Box sx={{ width: 40 }} />}
    </Box>
  );
}

function BottomNav({ view, go }) {
  const { totalItems } = useCart();
  const { guestMode, leaveGuestMode } = useCustomerAuth();
  const items = guestMode ? [
    ['home', 'Home', <Home />],
    ['lanchonetes', 'Lanchonetes', <Storefront />],
    ['cart', 'Carrinho', <Badge color="primary" badgeContent={totalItems}><ShoppingCart /></Badge>],
    ['orders', 'Pedidos', <ListAlt />],
    ['login', 'Entrar', <Person />]
  ] : [
    ['home', 'Home', <Home />],
    ['lanchonetes', 'Lanchonetes', <Storefront />],
    ['cart', 'Carrinho', <Badge color="primary" badgeContent={totalItems}><ShoppingCart /></Badge>],
    ['orders', 'Pedidos', <ListAlt />],
    ['chat', 'Mensagens', <Chat />],
    ['profile', 'Perfil', <Person />]
  ];
  return (
    <Box className="bottom-nav">
      {items.map(([target, label, icon], index) => (
        <Button key={`${target}-${index}`} className={view === target ? 'active' : ''} onClick={() => {
          if (target === 'login') {
            leaveGuestMode();
            return;
          }
          go(target);
        }}>
          {icon}
          <span>{label}</span>
        </Button>
      ))}
    </Box>
  );
}

function RoleBottomNav({ active, primaryLabel, primaryIcon, onPrimary, onProfile }) {
  return (
    <Box className="role-bottom-nav">
      <Button className={active !== 'profile' ? 'active' : ''} onClick={onPrimary}>
        {primaryIcon}
        <span>{primaryLabel}</span>
      </Button>
      <Button className={active === 'profile' ? 'active' : ''} onClick={onProfile}>
        <Person />
        <span>Perfil</span>
      </Button>
    </Box>
  );
}

function CustomerShell() {
  const { user, guestMode, loading } = useCustomerAuth();
  const [route, setRoute] = useState({ view: 'home', params: {} });

  const go = (view, params = {}) => setRoute({ view, params });

  if (loading) return <Box className="center"><CircularProgress /></Box>;
  if (!user && !guestMode) return <CustomerLogin />;
  if (user?.user_type === 'motoboy') {
    return route.view === 'profile'
      ? <ProfileView go={() => go('motoboy')} />
      : <MotoboyView go={go} />;
  }
  if (user?.user_type === 'waiter') {
    if (route.view === 'profile') return <ProfileView go={() => go('waiter')} />;
    if (route.view === 'store') return <StoreView params={route.params} go={go} />;
    if (route.view === 'cart') return <CartView go={go} params={route.params} />;
    return <WaiterView go={go} />;
  }

  let content;
  if (route.view === 'store') content = <StoreView params={route.params} go={go} />;
  else if (route.view === 'cart') content = <CartView go={go} params={route.params} />;
  else if (route.view === 'orders') content = <OrdersView go={go} />;
  else if (route.view === 'orderDetail') content = <OrderDetailView params={route.params} go={go} />;
  else if (route.view === 'profile') content = guestMode ? <HomeView go={go} /> : <ProfileView go={go} />;
  else if (route.view === 'chat') content = guestMode ? <HomeView go={go} /> : <ChatView go={go} />;
  else if (route.view === 'notifications') content = guestMode ? <HomeView go={go} /> : <NotificationsView go={go} />;
  else if (route.view === 'lanchonetes') content = <HomeView go={go} lanchonetesOnly />;
  else content = <HomeView go={go} />;

  return (
    <Box className="customer-app">
      {content}
      {!['store', 'orderDetail', 'notifications'].includes(route.view) && <BottomNav view={route.view} go={go} />}
    </Box>
  );
}

export default function CustomerApp() {
  return (
    <CustomerAuthProvider>
      <CartProvider>
        <CustomerStyles />
        <CustomerShell />
      </CartProvider>
    </CustomerAuthProvider>
  );
}

function CustomerStyles() {
  return (
    <style>{`
      html, body, #root { width: 100%; max-width: 100%; min-width: 0; margin: 0; overflow-x: hidden; }
      body { background: #f8fafc; }
      .customer-app { width: 100%; max-width: 100vw; min-width: 0; min-height: 100vh; background: #f8fafc; padding-bottom: 82px; color: #111827; overflow-x: hidden; }
      .customer-auth { min-height: 100vh; display: flex; justify-content: center; align-items: flex-start; padding: 24px 20px 40px; background: #E50914; }
      .customer-auth-shell { width: min(480px, 100%); }
      .customer-auth-header { text-align: center; padding: 40px 24px 28px; }
      .customer-auth-logo-ring { width: 88px; height: 88px; border-radius: 28px; padding: 3px; background: #fff; display: inline-flex; align-items: center; justify-content: center; margin: 0 auto 16px; box-shadow: 0 12px 28px rgba(100, 0, 0, 0.24); }
      .customer-auth-logo-inner { width: 100%; height: 100%; border-radius: 25px; background: #E50914; display: flex; align-items: center; justify-content: center; color: #fff; border: 2px solid #fff; }
      .customer-auth-brand { font-size: 26px !important; font-weight: 800 !important; color: #f8fafc !important; letter-spacing: 0.3px; }
      .customer-auth-tagline { font-size: 14px !important; color: rgba(255,255,255,.82) !important; margin-top: 6px !important; }
      .customer-auth-card { width: 100%; padding: 35px 25px !important; border-radius: 30px !important; text-align: left; background: #fff !important; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3) !important; border: 1px solid rgba(255, 255, 255, 0.2) !important; }
      .customer-auth-card-header { text-align: center; margin-bottom: 28px; }
      .customer-auth-card-header .MuiTypography-h5 { font-weight: 800 !important; color: #0f172a; font-size: 26px !important; margin-bottom: 8px !important; }
      .customer-auth-card-header .MuiTypography-body2 { color: #666 !important; font-size: 16px !important; }
      .brand-mark { width: 56px; height: 56px; border-radius: 16px; background: #10B981; color: #fff; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 12px; }
      .brand-mark.small { width: 42px; height: 42px; border-radius: 12px; margin: 0; background: rgba(255,255,255,0.2); }
      .auth-form { display: grid; gap: 18px; }
      .auth-input-group { display: grid; gap: 10px; }
      .auth-input-label { font-size: 16px; font-weight: 600; color: #1A1A2E; margin-left: 5px; text-align: left; }
      .auth-input-field .MuiOutlinedInput-root { background: #F8F9FA; border-radius: 15px; }
      .auth-input-field .MuiOutlinedInput-notchedOutline { border-color: #E9ECEF; border-width: 2px; }
      .auth-input-field .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color: #E50914; }
      .auth-input-field .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline { border-color: #E50914; }
      .auth-input-field .MuiOutlinedInput-input { padding: 16px 14px; font-weight: 500; color: #1A1A2E; }
      .auth-input-icon { color: #E50914 !important; }
      .auth-submit-btn { background: #E50914 !important; border-radius: 14px !important; padding: 14px !important; font-size: 18px !important; font-weight: 700 !important; text-transform: none !important; box-shadow: 0 8px 15px rgba(229, 9, 20, 0.24) !important; margin-top: 4px !important; }
      .auth-submit-btn:hover { background: #B20710 !important; box-shadow: 0 10px 20px rgba(229, 9, 20, 0.3) !important; }
      .auth-switch-row { display: flex; justify-content: center; align-items: center; gap: 8px; margin-top: 8px; flex-wrap: wrap; }
      .auth-switch-row .MuiTypography-root { color: #64748b; font-size: 15px; }
      .auth-switch-row .MuiButton-root { color: #E50914 !important; font-weight: 700 !important; text-transform: none !important; text-decoration: underline; padding: 0 !important; min-width: 0 !important; font-size: 16px !important; }
      .mode-switch { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; background: #eef2ff; border-radius: 12px; padding: 4px; margin-bottom: 4px; }
      .mode-switch .MuiButton-root { color: #334155; font-weight: 700; text-transform: none; border-radius: 10px; padding: 9px 8px; min-width: 0; }
      .mode-switch .MuiButton-root.active { background: #E50914; color: #fff; }
      .register-guide { display: grid; grid-template-columns: auto 1fr auto 1fr auto; align-items: center; gap: 8px; margin: -8px 0 22px; padding: 14px; border: 1px solid #fecaca; border-radius: 18px; background: #fff7f7; }
      .register-guide > div { display: grid; justify-items: center; gap: 5px; color: #7f1d1d; font-size: 10px; font-weight: 800; text-align: center; }
      .register-guide strong { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 50%; background: #E50914; color: #fff; font-size: 13px; }
      .register-guide i { height: 2px; background: #fecaca; }
      .register-guide .MuiTypography-root { grid-column: 1 / -1; margin-top: 5px; color: #7f1d1d; font-size: 11px; line-height: 1.45; text-align: center; }
      .app-header { width: 100%; box-sizing: border-box; background: linear-gradient(135deg, #047857 0%, #10B981 58%, #34D399 100%); color: #fff; padding: 22px 18px 24px; border-bottom-left-radius: 26px; border-bottom-right-radius: 26px; box-shadow: 0 14px 32px rgba(4, 120, 87, .18); }
      .lanchonetes-header { background: linear-gradient(135deg, #10B981, #059669, #34D399); color: #fff; padding: 42px 18px 54px; text-align: center; }
      .header-row, .location-row, .store-top, .section-title, .summary-row, .quantity-row, .detail-item { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
      .header-icon { color: #fff !important; background: rgba(255,255,255,0.18) !important; }
      .home-brand { display: flex; align-items: center; gap: 11px; }
      .home-brand-name { color: #fff; font-size: 19px !important; line-height: 1.1 !important; }
      .home-brand-tagline { color: rgba(255,255,255,.76); font-size: 10px !important; margin-top: 3px !important; }
      .home-welcome { margin-top: 28px; max-width: 920px; }
      .home-welcome-eyebrow { color: #a7f3d0; font-size: 10px !important; font-weight: 900 !important; letter-spacing: 1.4px !important; }
      .home-welcome-title { margin-top: 3px !important; color: #fff; font-size: 30px !important; line-height: 1.12 !important; font-weight: 950 !important; letter-spacing: -.8px !important; }
      .location-row { justify-content: flex-start !important; margin-top: 15px !important; background: rgba(255,255,255,0.14) !important; color: #fff !important; width: fit-content; max-width: 100%; padding: 7px 11px !important; border: 1px solid rgba(255,255,255,.18) !important; border-radius: 14px !important; text-transform: none !important; }
      .location-row .MuiTypography-caption { display: block; color: rgba(255,255,255,.72); font-size: 9px; line-height: 1; }
      .home-hero-search { margin-top: 22px !important; margin-bottom: -49px !important; position: relative; z-index: 2; }
      .home-hero-search .MuiOutlinedInput-root { min-height: 58px; background: #fff; color: #111827; border-radius: 18px; box-shadow: 0 16px 38px rgba(4, 80, 62, .26); }
      .home-hero-search .MuiOutlinedInput-notchedOutline { border: 0; }
      .home-hero-search .MuiInputAdornment-root { color: #10b981; }
      .screen-body { width: 100%; max-width: 920px; min-width: 0; box-sizing: border-box; margin: 0 auto; padding: 18px; display: grid; gap: 16px; }
      .cep-row, .coupon-row { display: flex; gap: 10px; align-items: center; }
      .cep-row .MuiTextField-root { flex: 1; }
      .location-search-card { display: grid; grid-template-columns: auto minmax(110px, 1fr) minmax(130px, 220px) auto auto; gap: 10px; align-items: center; padding: 10px 12px; border: 1px solid #d1fae5; border-radius: 18px !important; box-shadow: 0 10px 30px rgba(15, 23, 42, .08) !important; }
      .location-search-icon { width: 42px; height: 42px; display: grid; place-items: center; border-radius: 13px; background: #ecfdf5; color: #047857; }
      .location-search-copy { min-width: 0; display: grid; }
      .location-search-copy .MuiTypography-caption { color: #64748b; font-size: 9px; font-weight: 900; letter-spacing: .7px; }
      .location-search-card .MuiOutlinedInput-root { border-radius: 12px; background: #f8fafc; }
      .location-search-card .MuiButton-root { min-height: 40px; border-radius: 12px; font-weight: 900; text-transform: none; box-shadow: none; }
      .banner-strip { display: flex; gap: 12px; width: 100%; overflow: hidden; border-radius: 20px; background: #fff; box-shadow: 0 12px 30px rgba(15, 23, 42, .12); }
      .banner-strip img { min-width: 100%; width: 100%; height: 190px; object-fit: cover; border-radius: 20px; animation: bannerSlide 12s infinite; }
      @keyframes bannerSlide { 0%, 24% { transform: translateX(0); } 33%, 57% { transform: translateX(calc(-100% - 12px)); } 66%, 90% { transform: translateX(calc(-200% - 24px)); } 100% { transform: translateX(0); } }
      .chip-scroll { display: flex; gap: 8px; overflow-x: auto; padding: 3px 1px 6px; scrollbar-width: none; }
      .chip-scroll::-webkit-scrollbar { display: none; }
      .chip-scroll .MuiChip-root { height: 34px; border-radius: 12px; font-weight: 800; }
      .category-strip { display: flex; gap: 10px; overflow-x: auto; padding: 4px 1px 7px; scrollbar-width: none; }
      .category-strip::-webkit-scrollbar { display: none; }
      .category-item { min-width: 82px; height: 78px; display: grid; place-items: center; align-content: center; gap: 6px; border: 1px solid #e8eef1; border-radius: 18px; background: #fff; color: #475569; cursor: pointer; box-shadow: 0 6px 18px rgba(15,23,42,.05); transition: .18s ease; }
      .category-item > .category-emoji { font-size: 25px; line-height: 1; filter: saturate(.82); }
      .category-item .MuiTypography-root { max-width: 74px; overflow: hidden; text-overflow: ellipsis; font-size: 10px !important; line-height: 1.1 !important; white-space: nowrap; }
      .category-item.active { color: #047857; border-color: #6ee7b7; background: linear-gradient(145deg, #ecfdf5, #f0fdf4); box-shadow: 0 8px 22px rgba(16,185,129,.13); transform: translateY(-2px); }
      .home-section-kicker { color: #64748b; font-size: 10px !important; font-weight: 950 !important; letter-spacing: 1.2px !important; margin-bottom: -7px !important; }
      .store-list, .product-list, .order-list, .cart-items { display: grid; gap: 14px; }
      .home-screen-body .store-list { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .manual-delivery-fee-card { display: flex; align-items: center; gap: 14px; padding: 16px; margin-bottom: 18px; border: 2px dashed #10B981; cursor: pointer; }
      .manual-delivery-fee-card > svg { color: #059669; font-size: 34px; }
      .manual-delivery-fee-card.added { border-style: solid; background: #ECFDF5; }
      .store-card, .cart-item { display: flex; align-items: center; gap: 16px; min-width: 0; padding: 16px; border-radius: 20px !important; cursor: pointer; border: 1px solid #e8eef1; box-shadow: 0 8px 24px rgba(15, 23, 42, .07) !important; transition: transform .18s ease, box-shadow .18s ease; }
      .store-card:hover { transform: translateY(-2px); box-shadow: 0 14px 32px rgba(15, 23, 42, .11) !important; }
      .store-card.closed { opacity: .76; filter: grayscale(1); background: #f4f4f5; }
      .store-card.closed .store-avatar { filter: grayscale(1) contrast(1.08); }
      .store-image-wrap { position: relative; flex: 0 0 auto; }
      .store-avatar { width: 82px !important; height: 82px !important; border-radius: 18px !important; background: #ecfdf5 !important; box-shadow: inset 0 0 0 1px rgba(15, 23, 42, .05); }
      .availability-dot { position: absolute; right: -3px; bottom: -3px; width: 16px; height: 16px; border-radius: 50%; border: 3px solid #fff; }
      .availability-dot.open { background: #16a34a; }
      .availability-dot.closed { background: #ef4444; }
      .store-name { font-size: 16px !important; line-height: 1.2 !important; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      .store-description { margin-top: 3px !important; font-size: 13px !important; line-height: 1.35 !important; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      .delivery-line { margin-top: 6px !important; color: #059669; font-weight: 800 !important; font-size: 13px !important; }
      .store-facts { display: flex; column-gap: 7px; row-gap: 2px; flex-wrap: wrap; margin-top: 8px; color: #64748b; }
      .store-facts .delivery-fee { color: #047857; font-weight: 900; }
      .store-benefits { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 7px; }
      .store-benefits .MuiChip-root { height: 23px; background: #ecfdf5; color: #047857; font-size: 10px; font-weight: 900; }
      .home-empty-state { min-height: 240px; display: grid; place-items: center; align-content: center; gap: 10px; padding: 28px; text-align: center; border-radius: 20px !important; color: #94a3b8; }
      .home-order-card { display: flex; align-items: center; gap: 12px; padding: 15px; border-radius: 18px !important; cursor: pointer; }
      .home-order-card.active { background: #ecfdf5; border: 1px solid #a7f3d0; }
      .home-order-card.previous { background: #f5f3ff; border: 1px solid #ddd6fe; }
      .home-order-icon { width: 44px; height: 44px; flex: 0 0 44px; display: grid; place-items: center; border-radius: 14px; background: #10b981; color: #fff; }
      .home-order-card.previous .home-order-icon { background: #7c3aed; }
      .home-order-eyebrow { color: #64748b; font-size: 10px !important; font-weight: 900 !important; letter-spacing: .7px !important; }
      .center { min-height: 50vh; display: grid; place-items: center; }
      .store-hero { height: 240px; position: relative; background: #111827; overflow: hidden; }
      .store-hero img { width: 100%; height: 100%; object-fit: cover; opacity: .9; }
      .closed-store .store-hero img, .closed-store .store-logo, .closed-store .product-card img { filter: grayscale(1) contrast(1.08); }
      .closed-store .product-card { background: #f4f4f5; }
      .hero-actions { position: absolute; inset: 18px 18px auto; display: flex; justify-content: space-between; align-items: center; }
      .hero-actions .MuiIconButton-root { background: rgba(0,0,0,.55); color: #fff; }
      .store-info { max-width: 920px; margin: -42px auto 0; position: relative; padding: 0 18px 8px; text-align: center; }
      .store-logo { width: 86px !important; height: 86px !important; margin: 0 auto 10px; border: 4px solid #fff; box-shadow: 0 8px 26px rgba(0,0,0,.16); }
      .store-metas { display: flex; justify-content: center; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
      .sticky-chips { position: sticky; top: 0; z-index: 3; background: #f8fafc; padding: 8px 0; }
      .product-card { display: grid; grid-template-columns: 128px 1fr; min-height: 128px; overflow: hidden; border-radius: 18px !important; cursor: pointer; }
      .product-card img { width: 128px; height: 100%; object-fit: cover; background: #e5e7eb; }
      .product-info { padding: 14px; display: grid; align-content: space-between; gap: 8px; }
      .add-round { background: #10B981 !important; color: #fff !important; width: 34px; height: 34px; }
      .cart-bar { position: fixed !important; left: 50%; bottom: 90px; transform: translateX(-50%); width: min(900px, calc(100% - 32px)); border-radius: 16px !important; padding: 14px 18px !important; z-index: 10; justify-content: space-between !important; }
      .cart-bar span { font-weight: 900; }
      .categories-fab { position: fixed !important; right: 18px; bottom: 92px; z-index: 11; border-radius: 24px !important; background: #111827 !important; padding: 12px 18px !important; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.28) !important; }
      .categories-fab.with-cart { bottom: 162px; }
      .store-screen.has-cart .screen-body { padding-bottom: 96px; }
      .category-modal-list { display: grid; gap: 8px; }
      .product-modal-hero { height: 230px; position: relative; background: #111827; }
      .product-modal-hero img { width: 100%; height: 100%; object-fit: cover; }
      .product-modal-hero .MuiIconButton-root { position: absolute; top: 12px; right: 12px; background: rgba(0,0,0,.55); color: #fff; }
      .product-modal-content { display: grid; gap: 16px; padding-top: 18px !important; }
      .quantity-row { background: #f8fafc; padding: 10px 14px; border-radius: 14px; }
      .option-card, .checkout-card, .profile-card { padding: 16px; border-radius: 18px !important; }
      .option-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 12px; margin-top: 10px; cursor: pointer; }
      .option-row.active { border-color: #10B981; background: #ecfdf5; }
      .pizza-half-card { padding: 16px; border: 1px solid #f97316; border-radius: 18px !important; background: #fff7ed !important; }
      .pizza-mode-buttons { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 12px; }
      .pizza-flavor-list { display: grid; gap: 8px; margin-top: 14px; }
      .pizza-flavor-option { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px; border: 1px solid #fed7aa; border-radius: 12px; background: #fff; cursor: pointer; }
      .pizza-flavor-option.active { border: 2px solid #10B981; background: #ecfdf5; }
      .top-bar { min-height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 8px 14px; background: #fff; border-bottom: 1px solid #e5e7eb; position: sticky; top: 0; z-index: 9; }
      .checkout-card { display: grid; gap: 10px; }
      .qty-mini { display: flex; align-items: center; gap: 6px; }
      .payment-grid { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
      .payment-modal-grid { display: grid; gap: 12px; padding-top: 4px; }
      .payment-modal-grid .MuiButton-root { justify-content: center; padding: 16px; border-radius: 14px; font-weight: 900; text-transform: none; }
      .benefit-alert { border: 1px solid #34A853; border-radius: 12px !important; font-weight: 900; }
      .summary-row.total { font-size: 20px; color: #059669; }
      .empty-state { min-height: 70vh; display: grid; place-items: center; align-content: center; gap: 14px; color: #64748b; text-align: center; padding: 24px; }
      .pix-img { width: 220px; height: 220px; object-fit: contain; }
      .pix-placeholder { width: 180px; height: 180px; margin: 0 auto; border-radius: 18px; background: #ecfdf5; color: #059669; display: grid; place-items: center; font-size: 34px; font-weight: 900; }
      .pix-code { display: flex; align-items: center; gap: 8px; padding: 10px; }
      .order-card { border-radius: 18px !important; overflow: hidden; cursor: pointer; }
      .order-head { color: #fff; padding: 16px; display: flex; justify-content: space-between; align-items: center; }
      .detail-item { padding: 12px 0; border-bottom: 1px solid #e5e7eb; }
      .detail-item:last-child { border-bottom: 0; }
      .profile-card { text-align: center; }
      .profile-avatar { width: 78px !important; height: 78px !important; margin: 0 auto 12px; background: #10B981 !important; }
      .profile-hero { min-height: 170px; background: linear-gradient(135deg, #10B981, #059669, #34D399); color: #fff; display: flex; align-items: flex-start; justify-content: center; padding-top: 34px; position: relative; }
      .profile-back { position: absolute !important; left: 16px; top: 28px; color: #fff !important; background: rgba(255,255,255,.18) !important; }
      .profile-screen { margin-top: -74px; position: relative; z-index: 2; }
      .profile-main-card { text-align: center; padding: 26px 28px 30px; border-radius: 24px !important; display: grid; gap: 10px; }
      .profile-photo-wrap { position: relative; width: 128px; height: 128px; margin: 0 auto; }
      .profile-photo { width: 128px !important; height: 128px !important; background: #10B981 !important; font-size: 52px !important; }
      .profile-camera { position: absolute; right: 4px; bottom: 8px; width: 44px; height: 44px; border-radius: 50%; background: #10B981; color: #fff; display: grid; place-items: center; border: 4px solid #fff; }
      .profile-edit-button { border-radius: 14px !important; padding: 14px !important; font-weight: 900 !important; margin-top: 14px !important; }
      .profile-info-alert { border: 1px solid #99f6e4; border-radius: 16px !important; font-weight: 800; }
      .profile-section-card { padding: 24px; border-radius: 24px !important; display: grid; gap: 14px; }
      .profile-section-line { height: 3px; background: #10B981; border-radius: 2px; margin-bottom: 8px; }
      .profile-data-row { display: flex; align-items: center; gap: 16px; background: #fff; border-left: 5px solid #10B981; padding: 14px; border-radius: 16px; box-shadow: 0 4px 14px rgba(15, 23, 42, .1); }
      .profile-row-icon { width: 48px; height: 48px; display: grid; place-items: center; border-radius: 50%; background: #fff7ed; font-size: 24px; }
      .chat-screen { align-content: start; }
      .chat-card { overflow: hidden; border-radius: 20px !important; }
      .chat-store-header { display: flex; align-items: center; gap: 12px; padding: 14px; background: #ecfdf5; color: #065f46; }
      .chat-thread { display: grid; gap: 10px; padding: 14px; background: #f8fafc; }
      .chat-message-row { display: flex; justify-content: flex-start; }
      .chat-message-row.mine { justify-content: flex-end; }
      .chat-bubble { max-width: 84%; padding: 10px 13px; border: 1px solid #e2e8f0; border-radius: 16px 16px 16px 4px; background: #fff; }
      .chat-bubble.mine { border-color: #10b981; border-radius: 16px 16px 4px 16px; background: #10b981; color: #fff; }
      .chat-bubble .MuiTypography-caption { display: block; margin-top: 5px; opacity: .72; }
      .chat-composer { display: flex; align-items: center; gap: 8px; padding: 12px; border-top: 1px solid #e8eef1; }
      .whatsapp-support { border-radius: 16px !important; padding: 16px !important; font-weight: 900 !important; background: #22c55e !important; }
      .danger-zone { display: grid; gap: 14px; padding: 22px; border-radius: 20px !important; background: #fff7f7 !important; border: 1px solid #fecaca; color: #991b1b; }
      .danger-zone .MuiButton-root, .logout-card .MuiButton-root { border-radius: 14px !important; padding: 14px !important; font-weight: 900 !important; }
      .logout-card { padding: 22px; border-radius: 20px !important; display: grid; }
      .bottom-nav { position: fixed; left: 50%; transform: translateX(-50%); width: min(920px, 100%); bottom: 0; height: 76px; background: rgba(255,255,255,.97); backdrop-filter: blur(16px); border: 1px solid #e5e7eb; border-bottom: 0; border-radius: 20px 20px 0 0; display: grid; grid-template-columns: repeat(6, 1fr); z-index: 12; box-shadow: 0 -10px 30px rgba(15, 23, 42, .08); }
      .bottom-nav .MuiButton-root { min-width: 0; flex-direction: column; color: #64748b; font-size: 11px; border-radius: 14px; gap: 3px; margin: 5px 2px; }
      .bottom-nav .MuiButton-root.active { color: #E50914; font-weight: 900; }
      .bottom-nav span { font-size: 11px; text-transform: none; line-height: 1; }
      .role-bottom-nav { position: fixed; left: 0; right: 0; bottom: 0; height: 72px; background: #fff; border-top: 1px solid #e5e7eb; display: grid; grid-template-columns: repeat(2, 1fr); z-index: 12; }
      .role-bottom-nav .MuiButton-root { min-width: 0; flex-direction: column; color: #6b7280; font-size: 12px; border-radius: 0; gap: 3px; text-transform: none; }
      .role-bottom-nav .MuiButton-root.active { color: #E50914; font-weight: 900; }
      .role-bottom-nav span { font-size: 12px; line-height: 1; }
      .motoboy-header, .waiter-header { background: #E50914; color: #fff; padding: 24px 18px 34px; border-bottom-left-radius: 30px; border-bottom-right-radius: 30px; }
      .motoboy-avatar { background: #fff !important; color: #E50914 !important; width: 54px !important; height: 54px !important; }
      .motoboy-stats { display: grid; grid-template-columns: repeat(3, 1fr); text-align: center; margin: 22px 0; }
      .motoboy-stats strong { display: block; font-size: 28px; }
      .motoboy-stats span { opacity: .85; }
      .motoboy-tabs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; background: rgba(255,255,255,.16); border-radius: 18px; padding: 6px; }
      .motoboy-tabs .MuiButton-root { color: #fff; font-weight: 900; border-radius: 14px; text-transform: none; }
      .motoboy-tabs .MuiButton-root.active { background: #fff; color: #E50914; }
      .motoboy-income { grid-template-columns: 1fr auto; align-items: center; }
      .motoboy-screen-body { padding-bottom: 104px; }
      .motoboy-dialog-actions { display: grid !important; grid-template-columns: 1fr 1fr; gap: 8px; padding: 12px 16px 16px !important; }
      .motoboy-dialog-actions .MuiButton-root { width: 100%; min-width: 0; margin: 0 !important; white-space: nowrap; }
      .motoboy-dialog-actions .MuiButton-root:first-child { grid-column: 1 / -1; }
      .income-icon { background: #E50914; color: #fff; width: 74px; height: 74px; border-radius: 18px; display: grid; place-items: center; }
      .motoboy-empty { min-height: 280px; display: grid; place-items: center; align-content: center; gap: 16px; padding: 28px; color: #9ca3af; border-radius: 24px !important; text-align: center; }
      .waiter-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .mesa-card { min-height: 150px; display: grid; place-items: center; align-content: center; gap: 8px; border-radius: 24px !important; cursor: pointer; text-align: center; padding: 16px; }
      .mesa-card.free { background: #ecfdf5; color: #059669; }
      .mesa-card.quick { background: #fff1f2; color: #E50914; border: 2px dashed #E50914; }
      .mesa-card.busy { background: #fef2f2; color: #dc2626; }
      .app-header, .lanchonetes-header, .home-header, .profile-hero { background: #E50914; box-shadow: 0 14px 32px rgba(229,9,20,.18); }
      .category-item.active { color: #E50914; border-color: #fecaca; background: #fff1f2; box-shadow: 0 8px 22px rgba(229,9,20,.12); }
      .add-round, .profile-avatar, .profile-photo, .profile-camera, .home-order-icon { background: #E50914 !important; }
      .profile-section-line { background: #E50914; }
      .profile-data-row { border-left-color: #E50914; }
      .chat-store-header { background: #fff1f2; color: #991b1b; }
      .chat-bubble.mine { border-color: #E50914; background: #E50914; }
      .location-search-icon { background: #fff1f2; color: #E50914; }
      .home-hero-search .MuiInputAdornment-root { color: #E50914; }
      @media (max-width: 620px) {
        .home-header { min-height: 286px; padding: 20px 18px 34px; border-radius: 0 0 32px 32px; }
        .home-header .header-row { min-height: 52px; }
        .home-welcome { margin-top: 23px; }
        .home-welcome-title { max-width: 280px; font-size: 29px !important; }
        .home-hero-search { margin-top: 20px !important; margin-bottom: -62px !important; }
        .home-screen-body { gap: 16px; padding: 44px 14px 22px; }
        .screen-body { padding: 14px; }
        .screen-body.motoboy-screen-body { padding-bottom: 104px; }
        .cep-row, .coupon-row { align-items: stretch; flex-direction: column; }
        .location-search-card { grid-template-columns: auto minmax(0, 1fr) auto; margin-top: -18px; padding: 10px; }
        .location-search-card .location-search-copy { display: none; }
        .location-search-card .MuiTextField-root { min-width: 0; }
        .location-search-card > .MuiIconButton-root { display: none; }
        .banner-strip { border-radius: 14px; }
        .banner-strip img { height: 145px; }
        .store-card { gap: 12px; padding: 13px; }
        .store-avatar { width: 72px !important; height: 72px !important; }
        .store-card .MuiChip-root { flex: 0 0 auto; font-weight: 800; }
        .store-card > .MuiIconButton-root { padding: 6px; }
        .store-name { font-size: 15px !important; }
        .store-description { font-size: 12px !important; -webkit-line-clamp: 1; }
        .store-benefits { display: none; }
        .bottom-nav span { font-size: 9px; }
        .home-screen-body .store-list { grid-template-columns: 1fr; }
        .product-card { grid-template-columns: 112px 1fr; }
        .product-card img { width: 112px; }
        .store-top { align-items: flex-start; }
        .store-hero { height: 210px; }
        .cart-bar { bottom: 84px; }
        .categories-fab { bottom: 88px; }
        .categories-fab.with-cart { bottom: 156px; }
        .store-screen.has-cart .screen-body { padding-bottom: 88px; }
      }
    `}</style>
  );
}
